import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import moment from 'moment';
import url from 'url';
import { Formik, Form } from 'formik';
import lodashPick from 'lodash/fp/pick';
import lodashCompact from 'lodash/fp/compact';

import setNullEmptyValues from 'common/util/setNullEmptyValues';

import { USER_ROLE_LISTING_REVIEWER } from 'common/userRoles';

import {
  PROPERTY_TYPE_OPTION_TO_MAPPING_REALSAVVY_VALUES,
  PROPERTY_TYPE_OPTION_VALUE_APARTMENT,
  PROPERTY_TYPE_OPTION_APARTMENT,
} from 'common/propertyTypes';
import { PROPERTY_ATTRIBUTE_CASCADING_BEHAVIOR_OVERRIDE } from 'common/propertyAttributeConstants';

import emptyFunction from 'common/util/emptyFunction';
import { stateOptionItems } from 'common/util/stateCodes';
import { countryOptionItems } from 'common/util/countryCodes';
import {
  getListingAmenities,
  makeListingAddressStreetDisplay,
  makeListingFullAddressString,
  makeListingPhotosFromFloorPlan,
  isSelfShowingListing,
  isListingTenantScreening,
  isListingSunRise,
  getMarketSecurityDepositLabel,
} from 'common/listingHelpers';

import { isPreLeaseActive } from 'common/preLeaseHelpers';

import { formatPercentage } from 'common/formatters';

import {
  cleanPropertyAccessFields,
  getDueDateDisplay,
} from 'common/marketingPartnerListingHelpers';

import getListingFormFields, {
  floorPlanPropertyAttributesFieldgroup,
} from 'common/listingFormFields';

import { SUNLOCK_STATUS_INACTIVE } from 'common/sunlocks/sunlockConstants';

import typeCheck from 'common/util/typeCheck';
import pointInsidePolygon from 'common/util/pointInsidePolygon';
import getPetFieldsToNullify from 'common/util/getPetFieldsToNullify';

import {
  LISTING_STATUS_ACTIVE,
  LISTING_STATUS_PENDING,
  LISTING_STATUS_LEASED,
  LISTING_STATUS_LIST,
  LISTING_MLS_STATUS_NOT_AVAILABLE,
  LISTING_LOCKBOX_TYPES,
  LISTING_OCCUPANT_TYPES,
  LISTING_LOCKBOX_LOCATIONS,
  LISTING_DATA_SOURCE_SUNROOM,
  LISTING_STATUS_NOT_AVAILABLE,
  LISTING_APPLICATION_POLICY_FIRST,
  LISTING_APPLICATION_POLICY_BEST,
  LISTING_SR_BROKERAGE_MLS_NUMBER,
  LISTING_SR_BROKERAGE_NAME,
  LISTING_DISPLAY_TYPE_SINGLE_UNIT,
  LISTING_LINKS_TO_SCRAPE,
  LISTING_WALKTHROUGH_CONDITION_OTHER,
  LISTING_WALKTHROUGH_CONDITION_ITEMS,
  LISTING_WALKTHROUGH_CONDITION_READY,
  LISTING_YARD_SIGN_REDIRECTION_UTM_QUERY_PARAMETERS,
} from 'common/listingConstants';

import {
  PROPERTY_SETUP_ACCESS_METHOD_OPTION_ITEMS,
  PROPERTY_SETUP_ACCESS_METHOD_TYPE_CODE,
  PROPERTY_SETUP_ACCESS_METHOD_TYPE_KEY,
  PROPERTY_SETUP_ACCESS_TYPE_OPTION_ITEMS,
  PROPERTY_SETUP_ACCESS_TYPE_HIDDEN,
  PROPERTY_SETUP_ACCESS_TYPE_SUPRA_LOCKBOX,
  PROPERTY_SETUP_ACCESS_TYPE_CONTACT_FOR_KEY,
  PROPERTY_SETUP_ACCESS_TYPE_COMBO_LOCKBOX,
  MARKETING_PARTNER_LISTING_AGREEMENT_SIGNED_BY_SUNROOM,
  MARKETING_PARTNER_LISTING_INSTALL_STATUS_COMPLETED,
  MARKETING_PARTNER_LISTING_INSTALL_STATUS_IN_PROGRESS,
} from 'common/marketingPartnerListingConstants';

import {
  PARTNER_ID_STONE_OAK,
  PARTNER_ROLE_PROPERTY_MANAGER,
  PARTNER_TYPE_AGENT,
} from 'common/partnerConstants';

import { DATA_TABLE_SOURCE_ADMIN_LISTINGS_LIST } from 'common/dataTableConstants';

import {
  SR_API_ACTION_UPSERT_LISTING,
  SR_API_ACTION_DELETE_REDIRECTION,
  SR_API_ACTION_SET_REDIRECTION,
  SR_API_ACTION_SUNLOCK_ASSIGN_LISTING_ID,
  SR_API_ACTION_SUNLOCK_UNASSIGN_LISTING_ID,
  SR_API_ACTION_UPSERT_LISTING_LINKS,
  SR_API_ACTION_FUZZY_SEARCH_ADDRESS_STREET_SUFFIX,
  SR_API_ACTION_SUNLOCK_UPDATE_LOCK_CODES,
  SR_API_ACTION_GET_REDIRECTION,
  SR_API_ACTION_LISTING_GET,
  SR_API_ACTION_PROPERTY_ATTRIBUTE_GET_HIERARCHY,
} from 'api/actionTypes';

import {
  requiredActiveListingFields,
  petFieldsToNullify,
  apartmentSelfTourFlowFields,
  listingOwnersFieldgroup,
} from 'server/models/Listing/fieldgroups';

import {
  hideFieldIf,
  showFieldIf,
  createInitialValues,
  createFormValidationFn,
  trimStringValues,
} from 'client/helpers/formikHelpers';

import { actionDataTableReset } from 'client/helpers/dataTables';

import petWeightOptionItems from 'client/helpers/petWeightOptionItems';
import branch from 'client/core/branch';
import log from 'client/lib/log';
import postWithState from 'client/actions/postWithState';
import getSunlocksListAction from 'client/actions/Sunlock/getSunlocksList';
import getListingLinksAction from 'client/actions/Listing/getLinks';
import getTenantScreeningCriteriaAction from 'client/actions/TenantScreeningCriteria/getTenantScreeningCriteria';
import getListingById from 'client/actions/Listing/getById';
import getApartmentById from 'client/actions/Apartment/getById';
import getFloorPlansListByApartmentId from 'client/actions/FloorPlan/getListByApartmentId';
import updateLandlordAction from 'client/actions/Landlord/update';

import SrBox from 'client/components/SrBox';
import SrFormikField from 'client/components/SrFormikField';
import SrFindListingByAddressModal from 'client/components/SrFindListingByAddressModal';
import ButtonDeprecated, { BUTTON_THEME_YELLOW } from 'client/components/ButtonDeprecated';
import SrButton, { SR_BUTTON_THEME_ADOBE_YELLOW } from 'client/components/SrButton/SrButton';
import SrLink from 'client/components/SrLink/SrLink';
import SrLabel from 'client/components/SrLabel/SrLabel';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrDataTableMedia from 'client/components/SrDataTableMedia';
import SrAdminMediaListTable from 'client/components/SrAdminMediaListTable';
import SrEditListingTenantScreeningCriteriaCta from 'client/components/SrEditListingTenantScreeningCriteriaCta';
import SrSimpleMessageModal from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';
import SrRouteLeavingGuard from './SrRouteLeavingGuard';

const apartmentPropertyTypes =
  PROPERTY_TYPE_OPTION_TO_MAPPING_REALSAVVY_VALUES[PROPERTY_TYPE_OPTION_APARTMENT];

// WARNING(@d-oliveros): We need to strip dots out of the site names for Formik to work properly.
const listingLinkFormFieldIdToSiteNameMapping = LISTING_LINKS_TO_SCRAPE.reduce(
  (memo, { siteName }) => ({
    ...memo,
    [siteName.split('.')[0]]: siteName,
  }),
  {},
);

const debug = log.debug('sunroom:components:SrEditListingForm');

export const SR_EDIT_LISTING_FORM_VIEW_GROUP_ALL = 'SR_EDIT_LISTING_FORM_VIEW_GROUP_ALL';
export const SR_EDIT_LISTING_FORM_VIEW_GROUP_FIELD_OPTS =
  'SR_EDIT_LISTING_FORM_VIEW_GROUP_FIELD_OPTS';
export const SR_EDIT_LISTING_FORM_VIEW_GROUP_PHOTOGRAPHERS =
  'SR_EDIT_LISTING_FORM_VIEW_GROUP_PHOTOGRAPHERS';

const partnerRequiredAmenitiesWhenPhotosEnabledMapping = {
  hasGasStove: 'gasStovePhotoUrls',
  hasElectricStove: 'electricStovePhotoUrls',
  hasDishWasher: 'dishWasherPhotoUrls',
  hasRefrigerator: 'refrigeratorPhotoUrls',
  hasMicrowave: 'microwavePhotoUrls',
  hasWasher: 'washerPhotoUrls',
  hasDryer: 'dryerPhotoUrls',
  hasWasherDryerConnections: 'washerDryerConnectionsPhotoUrls',
};

function getPartnerAmenityRequiredFields({ formValues }) {
  const requiredFields = Object.keys(partnerRequiredAmenitiesWhenPhotosEnabledMapping);
  for (const requiredField of requiredFields) {
    if (formValues && formValues[requiredField] === true) {
      requiredFields.push(partnerRequiredAmenitiesWhenPhotosEnabledMapping[requiredField]);
    }
  }
  return requiredFields;
}

function _isSunRiseListing(listing) {
  if (!listing) {
    return false;
  }
  return isListingSunRise(listing);
}

function withRequiredValidationIfActive({ formFields, formValues, partnersList }) {
  const listingIsActive = formValues.status === LISTING_STATUS_ACTIVE;
  const partner =
    formValues && partnersList
      ? partnersList.find(({ managementCompanyName }) => {
          return formValues.listingManagementCompany === managementCompanyName;
        })
      : null;
  const partnerAmenityRequiredFields =
    partner && partner.amenityPhotosEnabled ? getPartnerAmenityRequiredFields({ formValues }) : [];

  const requiredActiveListingFieldsFull = [
    ...requiredActiveListingFields,
    ...partnerAmenityRequiredFields,
  ];

  return formFields.map((formField) => {
    let validations = formField.validations || [];
    if (listingIsActive && requiredActiveListingFieldsFull.includes(formField.id)) {
      if (formField.id.toLowerCase().includes('photourls')) {
        validations = validations.includes('nonEmptyArray')
          ? validations
          : ['nonEmptyArray', ...validations];
      }
      validations = validations.includes('required') ? validations : ['required', ...validations];
      if (formField.id === 'addressStreetSuffix' && formValues.addressStreetNoSuffix) {
        validations = validations.filter((validation) => validation !== 'required');
      }
    }
    if (!formValues.petsAllowed && petFieldsToNullify.includes(formField.id)) {
      validations = validations.filter((validationName) => validationName !== 'required');
    }
    return {
      ...formField,
      validations,
    };
  });
}

function withSunscreenValidations({ formFields, formValues }) {
  const SUNSCREEN_REQUIRED_FIELDS = [
    'price',
    'availableDate',
    'addressStreetNumber',
    'addressStreetName',
    'addressCity',
    'addressStateCode',
    'addressZip',
  ];

  const fields = formFields.map((formField) => {
    let validations = formField.validations || [];

    if (SUNSCREEN_REQUIRED_FIELDS.includes(formField.id)) {
      validations = ['required', ...validations];
    } else if (validations.includes('required')) {
      validations = validations.filter((validation) => validation !== 'required');
    }

    if (formField.id === 'locationLatitude' || formField.id === 'locationLongitude') {
      validations = validations.filter((validation) => validation !== 'coordinates');
    }
    if (formField.id === 'addressStreetSuffix' && !formValues.addressStreetNoSuffix) {
      validations = ['required', ...validations];
    }

    return {
      ...formField,
      validations,
    };
  });

  return fields;
}

function withDisabledFieldIdPropertyAttributeIsOverride(formFields, propertyAttributes) {
  const propertyAttributesByName = propertyAttributes.reduce(
    (memo, propertyAttribute) => ({
      ...memo,
      [propertyAttribute.name]: propertyAttribute,
    }),
    {},
  );
  return formFields.map((formField) => {
    const propertyAttribute = propertyAttributesByName[formField.id];
    if (!propertyAttribute) {
      return formField;
    }

    const properties = {
      ...(formField.properties || {}),
    };
    properties.disabled =
      properties.disabled ||
      propertyAttribute.cascadingBehavior === PROPERTY_ATTRIBUTE_CASCADING_BEHAVIOR_OVERRIDE;

    return {
      ...formField,
      properties,
    };
  });
}

function makeEditListingFormFields1({
  formValues,
  partnerId,
  partnerUsers,
  onApartmentChange,
  onApartmentBuildingChange,
  onListingStatusChange,
  onFloorPlanChange,
  streetSuffixMatches,
  showHiddenFields,
  requestStreetAutocompleteChoices,
  apartmentsList,
  partnersList,
  marketsList,
  floorPlansListByApartmentId,
} = {}) {
  typeCheck('apartmentsList::Array', apartmentsList);
  const isApartment = formValues.propertyType === PROPERTY_TYPE_OPTION_VALUE_APARTMENT;
  const isActive = formValues.status === LISTING_STATUS_ACTIVE;
  const partner = partnersList.find(({ managementCompanyName }) => {
    return formValues.listingManagementCompany === managementCompanyName;
  });

  const partnerApartmentsList =
    !isApartment || !partner
      ? []
      : apartmentsList.filter(({ partnerId }) => {
          return partner.id === partnerId;
        });

  const selectedPartnerApartment = partnerApartmentsList.find(({ name }) => {
    return name === formValues.apartmentName;
  });

  const floorPlans =
    (selectedPartnerApartment &&
      floorPlansListByApartmentId &&
      floorPlansListByApartmentId[selectedPartnerApartment.id]) ||
    [];

  const requireCoordinates =
    formValues.status === LISTING_STATUS_ACTIVE ||
    formValues.status === LISTING_STATUS_PENDING ||
    formValues.status === LISTING_STATUS_LEASED;

  const filteredPartnerUsers = partnerUsers
    ? partnerUsers.filter((x) => (x.roles || []).includes(PARTNER_ROLE_PROPERTY_MANAGER))
    : [];

  return [
    hideFieldIf(partnerId !== PARTNER_ID_STONE_OAK, {
      id: 'propertyManagerUserId',
      title: 'Partner User:',
      type: 'select',
      properties: {
        choices: [
          {
            label: 'None, Choose User',
            value: null,
          },
          ...filteredPartnerUsers.map((partnerUser) => ({
            label: `${partnerUser.user?.name}`,
            value: partnerUser.user?.id,
          })),
        ],
      },
    }),
    {
      id: 'promiseDate',
      title: 'Promise Date',
      type: 'short_text',
      properties: {
        disabled: true,
      },
    },
    ...getListingFormFields({
      fieldgroup: [
        'postToMls',
        'isSyndicated',
        'buyersAgentCommission',
        'mlsNumber',
        'addressStreetNumber',
        'addressStreetName',
        'addressStreetSuffix',
        'addressStreetNoSuffix',
        'apartmentUnit',
        'addressZip',
        'addressCity',
        'addressStateCode',
        'addressCounty',
        'addressCountryCode',
        'locationLatitude',
        'locationLongitude',
        'status',
        'mlsStatus',
        'cantTourFlag',
        'propertyType',
        'price',
        'yearBuilt',
        'legalDescription',
        'renovatedDate',
        'squareFeet',
        'stories',
        'bedrooms',
        'bathrooms',
        'apartmentName',
        'apartmentBuildingId',
        'floorPlanId',
        'apartmentUnitsCount',
        'floorLevel',
        'garageSpaces',
        'parkingSpaces',
        'hasGarage',
        'hasFence',
        'fenceDescription',
      ],
      updates: {
        buyersAgentCommission: {
          hideFieldIf: formValues.postToMls === 'false' || formValues.postToMls === false,
          validations: ['required'],
        },
        mlsStatus: {
          hideFieldIf: formValues.postToMls === 'false' || formValues.postToMls === false,
        },
        mlsNumber: {
          hideFieldIf: formValues.postToMls === 'false' || formValues.postToMls === false,
        },
        floorLevel: {
          hideFieldIf: !showHiddenFields,
        },
        renovatedDate: {
          hideFieldIf: !showHiddenFields,
        },
        status: {
          properties: {
            onChange: onListingStatusChange,
            choices: LISTING_STATUS_LIST.map((option) => ({
              label: option,
              value: option,
            })),
          },
        },
        addressStreetName: {
          validations: ['required'],
          properties: {
            disabled: !!(isApartment && partner),
          },
        },
        addressStreetSuffix: {
          properties: {
            choices: streetSuffixMatches,
            onSuggestionsFetchRequested: requestStreetAutocompleteChoices,
            disabled: !!(isApartment && partner),
          },
        },
        addressStreetNoSuffix: {
          properties: {
            disabled: !!(isApartment && partner),
          },
        },
        addressStreetNumber: {
          validations: ['required'],
          properties: {
            disabled: !!(isApartment && partner),
          },
        },
        addressZip: {
          validations: ['zipCode', 'required'],
          properties: {
            disabled: !!(isApartment && partner),
          },
        },
        addressCity: {
          validations: ['required'],
          properties: {
            disabled: !!(isApartment && partner),
          },
        },
        addressStateCode: {
          validations: ['required'],
          properties: {
            choices: stateOptionItems,
            disabled: !!(isApartment && partner),
          },
        },
        addressCounty: {
          properties: {
            disabled: !!(isApartment && partner),
          },
        },
        addressCountryCode: {
          validations: ['required'],
          properties: {
            choices: countryOptionItems,
            disabled: !!(isApartment && partner),
          },
        },
        locationLatitude: {
          properties: {
            longitude: formValues.locationLongitude,
            latitude: formValues.locationLatitude,
            marketsList: marketsList,
          },
          validations: ['coordinates', ...(requireCoordinates ? ['required'] : [])],
        },
        locationLongitude: {
          properties: {
            longitude: formValues.locationLongitude,
            latitude: formValues.locationLatitude,
            marketsList: marketsList,
          },
          validations: ['coordinates', ...(requireCoordinates ? ['required'] : [])],
        },
        apartmentName: {
          properties: {
            onChange: onApartmentChange,
            choices: partnerApartmentsList.map(({ name }) => ({
              label: name,
              value: name,
            })),
          },
          hideFieldIf: !isApartment,
        },
        apartmentBuildingId: {
          properties: {
            onChange: onApartmentBuildingChange || emptyFunction,
            choices: !selectedPartnerApartment
              ? []
              : selectedPartnerApartment.apartmentBuildings.map(({ id, addressStreetDisplay }) => ({
                  label: addressStreetDisplay,
                  value: id,
                })),
          },
          hideFieldIf: !isApartment,
        },
        floorPlanId: {
          properties: {
            onChange: onFloorPlanChange,
            choices: floorPlans.map(({ id, displayName }) => ({
              label: displayName,
              value: id,
            })),
          },
          validations: isActive && floorPlans.length ? ['required'] : [],
          hideFieldIf: !isApartment,
        },
        apartmentUnitsCount: {
          validations: isApartment && isActive ? ['required'] : [],
          hideFieldIf: !isApartment,
        },
        legalDescription: {
          validations: isActive && formValues.postToMls ? ['required'] : null,
        },
      },
    }),
  ];
}

function makeEditListingFormFields2({ showHiddenFields }) {
  return getListingFormFields({
    fieldgroup: [
      'hasPool',
      'dryerMaintainedByLandlord',
      'refrigeratorMaintainedByLandlord',
      'washerMaintainedByLandlord',
      'hasBambooFlooring',
      'hasBrick',
      'hasCarpetFlooring',
      'hasConcreteFlooring',
      'hasCorkFlooring',
      'hasLaminateFlooring',
      'hasLinoleumFlooring',
      'hasMarbleFlooring',
      'hasNoCarpetFlooring',
      'hasParquetFlooring',
      'hasSlateFlooring',
      'hasStoneFlooring',
      'hasTerrazzoFlooring',
      'hasTileFlooring',
      'hasVinylTileFlooring',
      'hasVinylFlooring',
      'hasVinylSheetFlooring',
      'hasWoodFlooring',
      'hasGarbageDisposal',
      'hasBasement',
      'hasPatio',
      'hasBalcony',
      'hasDeck',
      'isFurnished',
      'isNotFurnished',
      'smokeFree',
      'monthToMonthLeaseTerm',
      'rentToOwnLeaseTerm',
      'highSpeedInternetIncluded',
      'highSpeedInternetAvailable',
      'hasRampedEntry',
      'hasAccessibleParking',
      'hasLevelStyleDoorHandles',
      'environmentalControlsAboveFloor',
      'grabBarsInBathroom',
      'lowTresholdShower',
      'hasTTurnInBathroom',
      'accessibleBathroomVanities',
      'hasFrontLoadingKitchenAppliances',
      'hasAccessibleKitchenCounters',
      'hasClearFloorAreaFrontOfSink',
      'has32WideDoorways',
      'has36WideDoorways',
      'hasAirConditioning',
      'hasOven',
      'largeDogsAllowed',
      'electricityIncluded',
      'gasIncluded',
      'heatIncluded',
      'waterIncluded',
      'trashPickUpIncluded',
      'hasWaterSoftener',
    ],
    updates: {
      dryerMaintainedByLandlord: {
        hideFieldIf: !showHiddenFields,
      },
      refrigeratorMaintainedByLandlord: {
        hideFieldIf: !showHiddenFields,
      },
      washerMaintainedByLandlord: {
        hideFieldIf: !showHiddenFields,
      },
      hasBambooFlooring: {
        hideFieldIf: !showHiddenFields,
      },
      hasBrick: {
        hideFieldIf: !showHiddenFields,
      },
      hasCorkFlooring: {
        hideFieldIf: !showHiddenFields,
      },
      hasMarbleFlooring: {
        hideFieldIf: !showHiddenFields,
      },
      hasParquetFlooring: {
        hideFieldIf: !showHiddenFields,
      },
      hasSlateFlooring: {
        hideFieldIf: !showHiddenFields,
      },
      hasTerrazzoFlooring: {
        hideFieldIf: !showHiddenFields,
      },
      hasVinylFlooring: {
        hideFieldIf: !showHiddenFields,
      },
      hasVinylSheetFlooring: {
        hideFieldIf: !showHiddenFields,
      },
      hasGarbageDisposal: {
        hideFieldIf: !showHiddenFields,
      },
      hasBasement: {
        hideFieldIf: !showHiddenFields,
      },
      isFurnished: {
        hideFieldIf: !showHiddenFields,
      },
      monthToMonthLeaseTerm: {
        hideFieldIf: !showHiddenFields,
      },
      rentToOwnLeaseTerm: {
        hideFieldIf: !showHiddenFields,
      },
      highSpeedInternetIncluded: {
        hideFieldIf: !showHiddenFields,
      },
      highSpeedInternetAvailable: {
        hideFieldIf: !showHiddenFields,
      },
      hasRampedEntry: {
        hideFieldIf: !showHiddenFields,
      },
      hasAccessibleParking: {
        hideFieldIf: !showHiddenFields,
      },
      hasLevelStyleDoorHandles: {
        hideFieldIf: !showHiddenFields,
      },
      environmentalControlsAboveFloor: {
        hideFieldIf: !showHiddenFields,
      },
      grabBarsInBathroom: {
        hideFieldIf: !showHiddenFields,
      },
      lowTresholdShower: {
        hideFieldIf: !showHiddenFields,
      },
      hasTTurnInBathroom: {
        hideFieldIf: !showHiddenFields,
      },
      accessibleBathroomVanities: {
        hideFieldIf: !showHiddenFields,
      },
      hasFrontLoadingKitchenAppliances: {
        hideFieldIf: !showHiddenFields,
      },
      hasAccessibleKitchenCounters: {
        hideFieldIf: !showHiddenFields,
      },
      hasClearFloorAreaFrontOfSink: {
        hideFieldIf: !showHiddenFields,
      },
      has32WideDoorways: {
        hideFieldIf: !showHiddenFields,
      },
      has36WideDoorways: {
        hideFieldIf: !showHiddenFields,
      },
      largeDogsAllowed: {
        hideFieldIf: !showHiddenFields,
      },
      electricityIncluded: {
        hideFieldIf: !showHiddenFields,
      },
      gasIncluded: {
        hideFieldIf: !showHiddenFields,
      },
      heatIncluded: {
        hideFieldIf: !showHiddenFields,
      },
      waterIncluded: {
        hideFieldIf: !showHiddenFields,
      },
      trashPickUpIncluded: {
        hideFieldIf: !showHiddenFields,
      },
      hasWaterSoftener: {
        hideFieldIf: !showHiddenFields,
      },
    },
  });
}

function makeEditListingFormFields3({ showHiddenFields }) {
  const formFields = [
    {
      id: 'hasCentralHeating',
      title: 'Central Heat',
      type: 'checkbox',
    },
    hideFieldIf(!showHiddenFields, {
      id: 'hasNaturalGasHeating',
      title: 'Natural Gas Heat',
      type: 'checkbox',
    }),
    hideFieldIf(!showHiddenFields, {
      id: 'hasRadiatorHeating',
      title: 'Radiator Heat',
      type: 'checkbox',
    }),
    hideFieldIf(!showHiddenFields, {
      id: 'policyElectricPaid',
      title: 'Owner Pays Electric',
      type: 'checkbox',
    }),
    hideFieldIf(!showHiddenFields, {
      id: 'policyGasPaid',
      title: 'Owner Pays Gas',
      type: 'checkbox',
    }),
    hideFieldIf(!showHiddenFields, {
      id: 'policyTrashPaid',
      title: 'Owner Pays Trash',
      type: 'checkbox',
    }),
    hideFieldIf(!showHiddenFields, {
      id: 'policyWaterPaid',
      title: 'Owner Pays Water',
      type: 'checkbox',
    }),
    hideFieldIf(!showHiddenFields, {
      id: 'policyGardeningPaid',
      title: 'Owner Pays Lawn Care',
      type: 'checkbox',
    }),
    hideFieldIf(!showHiddenFields, {
      id: 'policyPestControlpaid',
      title: 'Owner Pays Pest Control',
      type: 'checkbox',
    }),
    {
      id: 'hasCentralAc',
      title: 'Central Air',
      type: 'checkbox',
    },
  ];

  return formFields;
}

function makeEditListingFormFields4({
  formValues,
  partnersList,
  landlord,
  partnerUsers,
  onPetsAllowedChange,
  marketsList,
}) {
  const filteredPartnerUsers = partnerUsers ? partnerUsers.filter((x) => x.agentMlsNumber) : [];

  const longitude = parseFloat(formValues.locationLongitude, 10);
  const latitude = parseFloat(formValues.locationLatitude, 10);

  let market = null;

  if (longitude && latitude) {
    for (const _market of marketsList) {
      const point = [longitude, latitude];
      if (pointInsidePolygon(point, _market.serviceZone.coordinates)) {
        market = _market;
        break;
      }
    }
  }
  const securityDepositLabel = getMarketSecurityDepositLabel(market, true);

  const formFields = [
    {
      id: 'listingManagementCompany',
      title: 'Partner',
      type: 'select',
      properties: {
        choices: partnersList.map(({ displayName, managementCompanyName }) => ({
          label: displayName,
          value: managementCompanyName,
        })),
      },
    },
    {
      id: 'coListingAgentMlsNumber',
      title: 'Co-Listing Agent',
      type: 'select',
      properties: {
        choices: [
          {
            label: 'None, Choose Agent',
            value: null,
          },
          ...filteredPartnerUsers.map((partnerUser) => ({
            label: `${partnerUser.user?.name} - ${partnerUser.agentMlsNumber}`,
            value: partnerUser.agentMlsNumber,
          })),
        ],
      },
    },
    showFieldIf(landlord, {
      id: 'landlordName',
      title: 'Landlord name',
      type: 'short_text',
      validatations: ['required'],
    }),
    showFieldIf(landlord, {
      id: 'landlordPhone',
      title: 'Landlord phone',
      type: 'phone',
      validations: ['required', 'phone'],
    }),
    showFieldIf(landlord, {
      id: 'landlordEmail',
      title: 'Landlord email',
      type: 'email',
      validatations: ['required', 'required'],
    }),
    {
      id: 'managementCompanyPhone',
      title: 'Management Company Phone',
      validations: ['phone'],
      type: 'phone',
    },
    {
      id: 'applicationFee',
      title: 'Application Fee',
      type: 'currency',
    },
    {
      id: 'partnerApplicationFee',
      title: 'Partner Application Fee (Cut)',
      type: 'currency',
    },
    {
      id: 'availableDate',
      title: 'Available Date',
      type: 'date_input',
    },
    {
      id: 'vacantDate',
      title: 'Vacant Date',
      type: 'date_input',
    },
    {
      id: 'hasHoa',
      title: 'HOA',
      type: 'checkbox',
    },
    {
      id: 'securityDeposit',
      title: securityDepositLabel,
      type: 'currency',
    },
    {
      id: 'applicationPolicy',
      title: 'Application Policy',
      type: 'select',
      properties: {
        choices: [
          {
            label: 'First Qualified Application',
            value: LISTING_APPLICATION_POLICY_FIRST,
          },
          {
            label: 'Best Qualified Application',
            value: LISTING_APPLICATION_POLICY_BEST,
          },
        ],
      },
    },
    {
      id: 'administrationFee',
      title: 'Administration Fee',
      type: 'currency',
    },
    {
      id: 'minLeaseMonths',
      title: 'Minimum Lease Months',
      type: 'number',
    },
    {
      id: 'acceptsGuarantor',
      title: 'Lease Guarantor?',
      type: 'checkbox',
    },
    {
      id: 'maxLeaseMonths',
      title: 'Maximum Lease Months',
      type: 'number',
    },
    {
      id: 'leaseLengthNegotiable',
      title: 'Lease Length Negotiable?',
      type: 'checkbox',
    },
    {
      id: 'section8',
      title: 'Enrolled in and accept Section 8?',
      type: 'checkbox',
    },
    {
      id: 'petsAllowed',
      title: 'Pets Allowed',
      type: 'checkbox',
      properties: {
        onChange: onPetsAllowedChange,
      },
    },
    {
      id: 'dogsAllowed',
      title: 'Dogs Allowed',
      type: 'checkbox',
    },
    {
      id: 'catsAllowed',
      title: 'Cats Allowed',
      type: 'checkbox',
    },
    {
      id: 'maximumPetsAllowed',
      title: 'Max Pets',
      type: 'number',
      validations:
        formValues.status === LISTING_STATUS_ACTIVE &&
        (formValues.petsAllowed || formValues.catsAllowed || formValues.dogsAllowed)
          ? ['required', 'positiveNumberString']
          : [],
    },
    {
      id: 'petsNegotiable',
      title: 'Pets Negotiable',
      type: 'checkbox',
    },
    {
      id: 'petWeight',
      title: 'Pet Weight',
      type: 'select',
      properties: {
        choices: petWeightOptionItems,
      },
    },
    {
      id: 'petDeposit',
      title: 'Pet Deposit',
      type: 'currency',
    },
    {
      id: 'petDepositPerPet',
      title: 'Pet Deposit Per Pet',
      type: 'checkbox',
    },
    {
      id: 'petFee',
      title: 'Pet Fee',
      type: 'currency',
    },
    {
      id: 'additionalPetFee',
      title: 'Additional Pet Fee',
      type: 'currency',
    },
    {
      id: 'monthlyPetRent',
      title: 'Monthly Pet Rent',
      type: 'currency',
    },
  ];

  return formFields;
}

function makeEditListingFormFields5({
  isNew,
  formValues,
  sunlocksList,
  partnersList = [],
  showHiddenFields,
} = {}) {
  typeCheck('partnersList::Array', partnersList);
  const hasSunlockSerialNumber = !!formValues.sunlockRaspberryPiSerialNumber;

  const formFields = [
    {
      id: 'lockboxType',
      title: 'Lockbox Type',
      type: 'select',
      properties: {
        choices: LISTING_LOCKBOX_TYPES.map((lockboxType) => ({
          label: lockboxType,
          value: lockboxType,
        })),
      },
    },
    {
      id: 'occupantType',
      title: 'Occupant Type',
      type: 'select',
      properties: {
        choices: LISTING_OCCUPANT_TYPES.map((occupantType) => ({
          label: occupantType,
          value: occupantType,
        })),
      },
    },
    {
      id: 'occupantPhone',
      title: 'Occupant Phone',
      validations: ['phone'],
      type: 'phone',
    },
    {
      id: 'occupantEmail',
      title: 'Occupant Email',
      validations: ['email'],
      type: 'email',
    },
    {
      id: 'occupantName',
      title: 'Occupant Name',
      type: 'short_text',
    },
    {
      id: 'fieldOpsScheduledDate',
      title: 'Field Ops Scheduled Date',
      type: 'date_picker',
      properties: {
        timeFormat: true,
        readOnly: false,
      },
    },
    {
      id: 'sunlockAllowed',
      title: 'Sunlock installable?',
      type: 'checkbox',
    },
    {
      id: 'sunlockRaspberryPiSerialNumber',
      title: 'Raspberry Pi Serial Number',
      type: 'autocomplete',
      properties: {
        placeholder: '000000003d1d1c36',
        choices: sunlocksList
          .filter(({ status }) => {
            return status === SUNLOCK_STATUS_INACTIVE;
          })
          .map(({ raspberryPiSerialNumber }) => {
            return raspberryPiSerialNumber;
          }),
        filterFunc: ({ value, choice }) => {
          const lowerCaseChoice = choice.toLowerCase();
          return value !== lowerCaseChoice && lowerCaseChoice.includes(value);
        },
      },
    },
    hideFieldIf(!showHiddenFields, {
      id: 'electronicLockCode',
      title: 'Electronic Lock Code',
      type: 'short_text',
    }),
    hideFieldIf(!showHiddenFields, {
      id: 'electronicLockInstructions',
      title: 'Electronic Lock Instructions',
      type: 'long_text',
    }),
    {
      id: 'lockCodeSetupLocked',
      title: 'Master Code Setup?',
      type: 'checkbox',
      validations: hasSunlockSerialNumber ? ['masterCodeSetupLocked'] : null,
    },
    hideFieldIf(!showHiddenFields, {
      id: 'igloohomeLockId',
      title: 'Igloohome Lock Id',
      type: 'short_text',
      validations: ['igloohomeLockId'],
    }),
    {
      id: 'accessMethod',
      title: 'Property Access Method',
      type: 'select',
      properties: {
        choices: PROPERTY_SETUP_ACCESS_METHOD_OPTION_ITEMS,
      },
    },
    hideFieldIf(formValues.accessMethod !== PROPERTY_SETUP_ACCESS_METHOD_TYPE_CODE, {
      id: 'propertyAccessCode',
      title: 'Property Access Code',
      type: 'short_text',
      validations: ['required'],
    }),
    hideFieldIf(formValues.accessMethod !== PROPERTY_SETUP_ACCESS_METHOD_TYPE_KEY, {
      id: 'propertySetupAccessType',
      title: 'Key Access',
      type: 'select',
      properties: {
        enableResetOption: true,
        choices: PROPERTY_SETUP_ACCESS_TYPE_OPTION_ITEMS,
      },
      validations: ['required'],
    }),
    {
      id: 'lockboxCode',
      title: 'Lockbox Code',
      type: 'short_text',
      validations:
        formValues.propertySetupAccessType === PROPERTY_SETUP_ACCESS_TYPE_COMBO_LOCKBOX
          ? ['required']
          : null,
    },
    {
      id: 'lockboxLocation',
      title: 'Lockbox Location',
      type: 'select',
      properties: {
        enableResetOption: true,
        choices: LISTING_LOCKBOX_LOCATIONS.map((lockboxLocation) => ({
          label: lockboxLocation,
          value: lockboxLocation,
        })),
      },
    },
    hideFieldIf(
      formValues.propertySetupAccessType !== PROPERTY_SETUP_ACCESS_TYPE_SUPRA_LOCKBOX ||
        formValues.accessMethod !== PROPERTY_SETUP_ACCESS_METHOD_TYPE_KEY,
      {
        id: 'supraCbsCode',
        title: 'Supra CBS Code',
        type: 'short_text',
        properties: {
          placeholder: '1234567',
          pattern: '[0-9]*',
        },
        validations: ['required', 'supraCbs'],
      },
    ),
    hideFieldIf(formValues.accessMethod !== PROPERTY_SETUP_ACCESS_METHOD_TYPE_KEY, {
      id: 'dateKeyExpectedAtListing',
      title: 'Date the key will be at the listing',
      type: 'date_picker',
    }),
    hideFieldIf(
      formValues.propertySetupAccessType !== PROPERTY_SETUP_ACCESS_TYPE_HIDDEN ||
        formValues.accessMethod !== PROPERTY_SETUP_ACCESS_METHOD_TYPE_KEY,
      {
        id: 'keyPickupDescription',
        title: 'Key Pickup Description',
        type: 'long_text',
        validations: ['required'],
      },
    ),
    hideFieldIf(formValues.accessMethod !== PROPERTY_SETUP_ACCESS_METHOD_TYPE_KEY, {
      id: 'keyPickupPhoto',
      title: 'Key Pickup Photo',
      type: 'file_single',
      properties: {
        fileAccept: '*',
        filePath: '/listing-uploads/key-pickup-photos/{uuid}',
      },
    }),
    hideFieldIf(
      formValues.propertySetupAccessType !== PROPERTY_SETUP_ACCESS_TYPE_CONTACT_FOR_KEY ||
        formValues.accessMethod !== PROPERTY_SETUP_ACCESS_METHOD_TYPE_KEY,
      {
        id: 'propertyAccessContactName',
        title: 'Key Contact Name',
        properties: {
          placeholder: 'Key contact name',
        },
        type: 'short_text',
        validations: ['required'],
      },
    ),
    hideFieldIf(
      formValues.propertySetupAccessType !== PROPERTY_SETUP_ACCESS_TYPE_CONTACT_FOR_KEY ||
        formValues.accessMethod !== PROPERTY_SETUP_ACCESS_METHOD_TYPE_KEY,
      {
        id: 'propertyAccessContactPhone',
        title: 'Key Contact Phone',
        properties: {
          placeholder: 'Key contact phone',
        },
        type: 'short_text',
        validations: ['required'],
      },
    ),
    hideFieldIf(
      formValues.propertySetupAccessType !== PROPERTY_SETUP_ACCESS_TYPE_CONTACT_FOR_KEY ||
        formValues.accessMethod !== PROPERTY_SETUP_ACCESS_METHOD_TYPE_KEY,
      {
        id: 'propertyAccessContactEmail',
        title: 'Key Contact Email',
        properties: {
          placeholder: 'Key contact email',
        },
        type: 'short_text',
        validations: ['required'],
      },
    ),
    hideFieldIf(!showHiddenFields, {
      id: 'alarmCode',
      title: 'Alarm Code',
      type: 'short_text',
    }),
    {
      id: 'gateCode',
      title: 'Security (gate code)',
      type: 'short_text',
    },
    {
      id: 'yardSignAllowed',
      title: 'Yard sign allowed?',
      type: 'checkbox',
    },
    {
      id: 'redirectionSourceUrl',
      title: 'Yard Sign URL',
      type: 'short_text',
      validations:
        formValues.status === LISTING_STATUS_ACTIVE &&
        formValues.yardSignAllowed &&
        formValues.propertyType !== PROPERTY_TYPE_OPTION_VALUE_APARTMENT
          ? ['required']
          : [],
      properties: {
        disabled: isNew,
        placeholder: 'A6',
      },
    },
    {
      id: 'showingRemarks',
      title: 'Private Remarks',
      type: 'long_text',
    },
    {
      id: 'showingInstructions',
      title: 'Special Showing Instructions',
      type: 'long_text',
    },
    {
      id: 'description',
      title: 'Marketing Description',
      type: 'long_text',
    },
    {
      id: 'partnerMarketingNotes',
      title: 'Partner Marketing Notes',
      type: 'long_text',
    },
    {
      id: 'requirePhotos',
      title: 'Photos Needed?',
      type: 'checkbox',
    },
  ];

  return formFields;
}

function makeEditListingWalkToUnitFormFields({
  formValues,
  partnersList = [],
  apartmentsList = [],
}) {
  const partner =
    formValues.listingManagementCompany &&
    partnersList.find(({ managementCompanyName }) => {
      return formValues.listingManagementCompany === managementCompanyName;
    });
  const apartment =
    partner &&
    formValues.apartmentName &&
    apartmentsList.find(({ partnerId, name }) => {
      return partner.id === partnerId && name === formValues.apartmentName;
    });

  return [
    {
      id: 'walkToUnitInstructions',
      title: 'Walk To Unit Instructions',
      type: 'long_text',
      validations:
        isSelfShowingListing(formValues) &&
        apartment &&
        apartment.selfTourFlowEnabled &&
        formValues.status === LISTING_STATUS_ACTIVE
          ? ['required']
          : [],
    },
    {
      id: 'walkToUnitPhotoUrls',
      title: 'Walk To Unit Photos',
      type: 'file_multi',
      properties: {
        filePath: '/listing-uploads/walk-to-unit-photos/{uuid}',
        fileAccept: 'image/*',
      },
      validations:
        isSelfShowingListing(formValues) &&
        apartment &&
        apartment.selfTourFlowEnabled &&
        formValues.status === LISTING_STATUS_ACTIVE
          ? ['nonEmptyArray']
          : [],
    },
  ];
}

function makeEditListingFormFields6({ formValues, onExternalLinksLockedChange }) {
  const formFields = [
    {
      id: 'externalLinksLocked',
      title: 'Lock Urls',
      type: 'checkbox',
      properties: {
        onChange: onExternalLinksLockedChange,
      },
    },
  ];

  for (const formFieldId of Object.keys(listingLinkFormFieldIdToSiteNameMapping)) {
    const hideField =
      formFieldId === 'MLS' && (formValues.postToMls === 'false' || formValues.postToMls === false);
    const listingLink = (formValues.listingLinks || {})[formFieldId];

    formFields.push(
      hideFieldIf(hideField, {
        id: `listingLinks.${formFieldId}`,
        title: listingLinkFormFieldIdToSiteNameMapping[formFieldId],
        type: 'short_text',
        validations: ['url'],
        properties: {
          disabled: !!formValues.externalLinksLocked,
        },
      }),
    );

    formFields.push(
      hideFieldIf(hideField, {
        id: `listingLinks.${formFieldId}Reviewed`,
        title: 'I checked it’s listed by Sunroom',
        type: 'checkbox',
        validations: listingLink ? ['requiredCheck'] : [],
        properties: {
          disabled: !!formValues.externalLinksLocked,
        },
      }),
    );
  }

  return formFields;
}

function makeEditListingFormFields7(params) {
  const formFields = [
    {
      id: 'photoUrls',
      title: 'Photos for marketing (max. 40 photos)',
      type: 'file_multi',
      properties: {
        filePath: '/listing-uploads/photos/{uuid}',
        fileAccept: 'image/*, .heic',
        maxFiles: 40,
      },
    },
    hideFieldIf(params.formViewGroup === SR_EDIT_LISTING_FORM_VIEW_GROUP_PHOTOGRAPHERS, {
      id: 'photosAutoenhance',
      title: 'Auto-edit photos when uploading',
      type: 'checkbox',
    }),
    hideFieldIf(!params.formValues?.linkToExistingPhotos, {
      id: 'linkToExistingPhotos',
      type: 'short_text',
      title: 'Where to get photos: ',
    }),
    hideFieldIf(params.formViewGroup === SR_EDIT_LISTING_FORM_VIEW_GROUP_PHOTOGRAPHERS, {
      id: 'photoLiveStatus',
      title: 'Photo Live Status',
      type: 'select',
      properties: {
        choices: [
          {
            label: 'In progress',
            value: MARKETING_PARTNER_LISTING_INSTALL_STATUS_IN_PROGRESS,
          },
          {
            label: 'Completed (send email to Partner)',
            value: MARKETING_PARTNER_LISTING_INSTALL_STATUS_COMPLETED,
          },
        ],
      },
    }),
    hideFieldIf(
      params.formValues?.photoLiveStatus !== MARKETING_PARTNER_LISTING_INSTALL_STATUS_COMPLETED ||
        params.formViewGroup === SR_EDIT_LISTING_FORM_VIEW_GROUP_PHOTOGRAPHERS,
      {
        id: 'photoLiveDate',
        title: 'Date Photos Completed',
        type: 'date_picker',
        properties: {
          placeholderValue: 'MM/DD/YYYY',
          placeholder: 'MM/DD/YYYY',
          dateFormat: 'MM/DD/YYYY',
        },
      },
    ),
    hideFieldIf(params.formViewGroup === SR_EDIT_LISTING_FORM_VIEW_GROUP_PHOTOGRAPHERS, {
      id: 'photoLiveNotes',
      title: 'Note For Partner',
      type: 'long_text',
    }),
    hideFieldIf(params.formViewGroup === SR_EDIT_LISTING_FORM_VIEW_GROUP_FIELD_OPTS, {
      id: 'washerDryerPhotoUrls',
      title: 'Washer/Dryer Photo',
      type: 'file_multi',
      properties: {
        filePath: '/listing-uploads/washer-dryer-photos/{uuid}',
        fileAccept: 'image/*',
      },
    }),
    {
      id: 'lockboxPhotoUrls',
      title: 'Supra Lockbox Photo',
      type: 'file_multi',
      properties: {
        filePath: '/listing-uploads/supra-lockbox/{uuid}',
        fileAccept: 'image/*',
      },
    },
    {
      id: 'comboPhotoUrls',
      title: 'Combo Lockbox Photo',
      type: 'file_multi',
      properties: {
        filePath: '/listing-uploads/combo-lockbox/{uuid}',
        fileAccept: 'image/*',
      },
    },
    {
      id: 'yardSignPhotoUrls',
      title: 'Yard Sign Photo',
      type: 'file_multi',
      properties: {
        filePath: '/listing-uploads/yard-signs/{uuid}',
        fileAccept: 'image/*',
      },
    },
    {
      id: 'sunlockPhotoUrls',
      title: 'Sunlock Photo',
      type: 'file_multi',
      properties: {
        filePath: '/listing-uploads/sunlock/{uuid}',
        fileAccept: 'image/*',
      },
    },
    hideFieldIf(
      params.formViewGroup === SR_EDIT_LISTING_FORM_VIEW_GROUP_FIELD_OPTS ||
        params.formViewGroup === SR_EDIT_LISTING_FORM_VIEW_GROUP_PHOTOGRAPHERS,
      {
        id: 'tenantCriteriaFileUrls',
        title: 'Tenant Criteria Files',
        type: 'file_multi',
        properties: {
          filePath: '/listing-uploads/tenant-criteria/{uuid}',
          fileAccept: '*',
        },
      },
    ),
  ];

  return formFields;
}

function makeEditListingFormFields8({
  onWalkthroughConditionsChange,
  formValues: { walkthroughConditions = [] },
}) {
  const formFields = [
    {
      id: 'walkthroughConditions',
      title: 'Ready to be listed?',
      type: 'multiple_choice',
      properties: {
        choices: LISTING_WALKTHROUGH_CONDITION_ITEMS,
        onChange: onWalkthroughConditionsChange,
      },
    },
    {
      id: 'walkthroughExtraCondition',
      title: 'Not ready reason other ',
      type: 'long_text',
      validations: walkthroughConditions.includes(LISTING_WALKTHROUGH_CONDITION_OTHER)
        ? ['required']
        : [],
    },
    {
      id: 'walkthroughNotes',
      title: 'Walkthrough notes',
      type: 'long_text',
    },
    {
      id: 'walkthroughPartnerResolvedNotes',
      title: 'Resolved notes from Partner',
      type: 'long_text',
      properties: {
        disabled: true,
      },
    },
    {
      id: 'walkthroughPhotoUrls',
      title: 'Walkthrough Photos',
      type: 'file_multi',
      properties: {
        filePath: '/listing-uploads/walkthrough/{uuid}',
        fileAccept: 'image/*',
      },
    },
    {
      id: 'sunlockInstallationNotes',
      title: 'Sunlock installation notes',
      type: 'long_text',
    },
  ];

  return formFields;
}

function makeEditListingFormFields9(params) {
  return getListingFormFields({
    fieldgroup: [
      'hasGasStove',
      'gasStovePhotoUrls',
      'hasElectricStove',
      'electricStovePhotoUrls',
      'hasDishWasher',
      'dishWasherPhotoUrls',
      'hasRefrigerator',
      'refrigeratorPhotoUrls',
      'hasMicrowave',
      'microwavePhotoUrls',
      'hasWasher',
      'washerPhotoUrls',
      'hasDryer',
      'dryerPhotoUrls',
      'hasWasherDryerConnections',
      'washerDryerConnectionsPhotoUrls',
    ].filter((fieldId) => {
      return (
        params.formViewGroup === SR_EDIT_LISTING_FORM_VIEW_GROUP_ALL || !fieldId.includes('has')
      );
    }),
  });
}

function makePropertyOwnersFields({ isSunRiseListing }) {
  const updates = {};
  const requiredSunRiseFields = ['propertyOwnerName', 'propertyOwnerEmail', 'propertyOwnerPhone'];

  for (const field of listingOwnersFieldgroup) {
    updates[field] = {
      hideFieldIf: !isSunRiseListing,
      validations: requiredSunRiseFields.includes(field) && isSunRiseListing ? ['required'] : [],
    };
  }

  return getListingFormFields({
    fieldgroup: listingOwnersFieldgroup,
    updates: updates,
  });
}

function makeEditListingFeeFormFields({ showHiddenFields }) {
  return getListingFormFields({
    fieldgroup: [
      'hasCommunalWasherDryer',
      'hasDogWalkPark',
      'kitchenCountertopMaterial',
      'hasBusinessCenter',
      'hasConferenceRoom',
      'hasJoggingTrail',
      'hasRoofDeck',
      'elevatorInBuilding',
      'hasStackedWasherDryer',
      'hasOnlinePayments',
      'floorLevel',
      'hasWheelchairAccessible',
      'coveredParkingCount',
      'surfaceParkingCount',
      'garageAttachedParkingSpots',
      'garageDetachedParkingSpots',
      'hasOnSiteManagement',
      'hasFencedYards',
      'hasYard',
      'hasControlledBuildingAccess',
      'hasCeilingFans',
      'hasFireplace',
      'hasExtraStorage',
      'hasWalkInClosets',
      'kitchenAppliancesColor',
      'hasShowerTub',
      'numberOfFloors',
      'valetTrashFee',
      'pestControlFee',
      'wasteWaterFee',
      'monthlyCommunityFee',
      'monthlyCommunityFeeDescription',
      'carportFee',
      'reservedParkingSpotFee',
      'bikeRackFee',
      'electricFee',
      'gasFee',
      'waterAndSewerFee',
      'cableAndInternetCombinedFee',
      'cableFee',
      'internetFee',
      'packageLockerSystemFee',
      'coveredParkingFee',
      'storageFee',
      'surfaceParkingFee',
      'garageAttachedParkingSpotFee',
      'garageDetachedParkingSpotFee',
    ],
    updates: {
      hasCommunalWasherDryer: {
        hideFieldIf: !showHiddenFields,
      },
      hasDogWalkPark: {
        hideFieldIf: !showHiddenFields,
      },
      kitchenCountertopMaterial: {
        hideFieldIf: !showHiddenFields,
      },
      hasBusinessCenter: {
        hideFieldIf: !showHiddenFields,
      },
      hasConferenceRoom: {
        hideFieldIf: !showHiddenFields,
      },
      hasJoggingTrail: {
        hideFieldIf: !showHiddenFields,
      },
      hasRoofDeck: {
        hideFieldIf: !showHiddenFields,
      },
      elevatorInBuilding: {
        hideFieldIf: !showHiddenFields,
      },
      hasOnlinePayments: {
        hideFieldIf: !showHiddenFields,
      },
      floorLevel: {
        hideFieldIf: !showHiddenFields,
      },
      hasWheelchairAccessible: {
        hideFieldIf: !showHiddenFields,
      },
      coveredParkingCount: {
        hideFieldIf: !showHiddenFields,
      },
      surfaceParkingCount: {
        hideFieldIf: !showHiddenFields,
      },
      garageAttachedParkingSpots: {
        hideFieldIf: !showHiddenFields,
      },
      garageDetachedParkingSpots: {
        hideFieldIf: !showHiddenFields,
      },
      hasOnSiteManagement: {
        hideFieldIf: !showHiddenFields,
      },
      hasFencedYards: {
        hideFieldIf: !showHiddenFields,
      },
      hasControlledBuildingAccess: {
        hideFieldIf: !showHiddenFields,
      },
      hasCeilingFans: {
        hideFieldIf: !showHiddenFields,
      },
      hasFireplace: {
        hideFieldIf: !showHiddenFields,
      },
      hasExtraStorage: {
        hideFieldIf: !showHiddenFields,
      },
      hasWalkInClosets: {
        hideFieldIf: !showHiddenFields,
      },
      kitchenAppliancesColor: {
        hideFieldIf: !showHiddenFields,
      },
      hasShowerTub: {
        hideFieldIf: !showHiddenFields,
      },
      numberOfFloors: {
        hideFieldIf: !showHiddenFields,
      },
      valetTrashFee: {
        hideFieldIf: !showHiddenFields,
      },
      pestControlFee: {
        hideFieldIf: !showHiddenFields,
      },
      wasteWaterFee: {
        hideFieldIf: !showHiddenFields,
      },
      monthlyCommunityFee: {
        hideFieldIf: !showHiddenFields,
      },
      monthlyCommunityFeeDescription: {
        hideFieldIf: !showHiddenFields,
      },
      carportFee: {
        hideFieldIf: !showHiddenFields,
      },
      reservedParkingSpotFee: {
        hideFieldIf: !showHiddenFields,
      },
      bikeRackFee: {
        hideFieldIf: !showHiddenFields,
      },
      electricFee: {
        hideFieldIf: !showHiddenFields,
      },
      gasFee: {
        hideFieldIf: !showHiddenFields,
      },
      waterAndSewerFee: {
        hideFieldIf: !showHiddenFields,
      },
      cableAndInternetCombinedFee: {
        hideFieldIf: !showHiddenFields,
      },
      cableFee: {
        hideFieldIf: !showHiddenFields,
      },
      internetFee: {
        hideFieldIf: !showHiddenFields,
      },
      packageLockerSystemFee: {
        hideFieldIf: !showHiddenFields,
      },
      coveredParkingFee: {
        hideFieldIf: !showHiddenFields,
      },
      storageFee: {
        hideFieldIf: !showHiddenFields,
      },
      surfaceParkingFee: {
        hideFieldIf: !showHiddenFields,
      },
      garageAttachedParkingSpotFee: {
        hideFieldIf: !showHiddenFields,
      },
      garageDetachedParkingSpotFee: {
        hideFieldIf: !showHiddenFields,
      },
    },
  });
}

const SR_EDIT_LISTING_FORM_VIEW_GROUP_TO_FORM_FIELD_GROUPS_MAPPING = {
  [SR_EDIT_LISTING_FORM_VIEW_GROUP_ALL]: [
    makeEditListingFormFields1,
    makeEditListingFormFields2,
    makeEditListingFormFields3,
    makePropertyOwnersFields,
    makeEditListingFormFields4,
    makeEditListingFeeFormFields,
    makeEditListingFormFields5,
    makeEditListingWalkToUnitFormFields,
    makeEditListingFormFields8,
    makeEditListingFormFields6,
    makeEditListingFormFields7,
    makeEditListingFormFields9,
  ],
  [SR_EDIT_LISTING_FORM_VIEW_GROUP_FIELD_OPTS]: [
    makeEditListingFormFields7,
    makeEditListingFormFields9,
  ],
  [SR_EDIT_LISTING_FORM_VIEW_GROUP_PHOTOGRAPHERS]: [
    makeEditListingFormFields7,
    makeEditListingFormFields9,
  ],
};

function makeEditListingFormFields(params) {
  const formFieldGroups =
    SR_EDIT_LISTING_FORM_VIEW_GROUP_TO_FORM_FIELD_GROUPS_MAPPING[params.formViewGroup];
  const formFields = formFieldGroups.reduce((memo, formFieldGroup) => {
    memo.push(...formFieldGroup(params));
    return memo;
  }, []);

  if (params.isTenantScreening) {
    return withSunscreenValidations({ formFields, ...params });
  }

  if (params.formFieldGroup !== SR_EDIT_LISTING_FORM_VIEW_GROUP_ALL) {
    return formFields;
  }
  return withRequiredValidationIfActive({ formFields, ...params });
}

function makeListingLinksFormValuesFromSrListingLinks(listingLinks) {
  typeCheck('listingLinks::Array', listingLinks);
  return listingLinks.reduce(
    (memo, { siteName, linkUrl, wasReviewed }) => ({
      ...memo,
      [siteName.split('.')[0]]: linkUrl,
      [`${siteName.split('.')[0]}Reviewed`]: wasReviewed,
    }),
    {},
  );
}

function _onScrollToField(event) {
  const fieldId = event.target.dataset.fieldId;
  if (process.browser) {
    const [element] = global.document.querySelectorAll(`[name="${fieldId}"]`);
    if (element && typeof element.scrollIntoView === 'function') {
      element.scrollIntoView();
    }
  }
}

function _isTenantScreening(listing) {
  if (!listing) {
    return false;
  }
  return isListingTenantScreening(listing);
}

const newListingDefaultValues = {
  addressCountryCode: 'US',
  addressStateCode: 'TX',
  status: LISTING_STATUS_NOT_AVAILABLE,
  mlsStatus: LISTING_MLS_STATUS_NOT_AVAILABLE,
  photoUrls: [],
};

const fieldsToExcludeFromListingFuzzySearchCopy = [
  'lockboxType',
  'occupantType',
  'occupantPhone',
  'occupantName',
  'lockboxCode',
  'lockboxLocation',
  'mlsNumber',
  'availableDate',
];

@branch({
  currentUser: ['currentUser'],
  floorPlansListByApartmentId: ['floorPlans', 'listByApartmentId'],
  appUrl: ['env', 'APP_URL'],
})
class SrEditListingForm extends Component {
  static propTypes = {
    currentUser: PropTypes.object.isRequired,
    appUrl: PropTypes.string.isRequired,
    listing: PropTypes.object,
    listingLinks: PropTypes.array.isRequired,
    listingRedirection: PropTypes.object,
    onSubmit: PropTypes.func.isRequired,
    dispatch: PropTypes.func.isRequired,
    partnersList: PropTypes.array.isRequired,
    marketsList: PropTypes.array.isRequired,
    formViewGroup: PropTypes.oneOf(
      Object.keys(SR_EDIT_LISTING_FORM_VIEW_GROUP_TO_FORM_FIELD_GROUPS_MAPPING),
    ).isRequired,
    floorPlansListByApartmentId: PropTypes.object.isRequired,
    accountManager: PropTypes.object,
    leasingManager: PropTypes.object,
    landlord: PropTypes.object,
    partnerUsers: PropTypes.array.isRequired,
    marketingPartnerListing: PropTypes.object,
    jobItemsInstalled: PropTypes.object,
  };

  constructor(props) {
    super(props);
    this.state = {
      isBusy: false,
      showSubmittedLabel: false,
      showPrepopulateListingForm: false,
      showHiddenFields: false,
      streetSuffixMatches: [],
      showMissingTenantScreeningCriteriaAlert: false,
    };
    this._isUnmounting = false;
    this._onServerValidations = this._onServerValidations.bind(this);
  }

  componentDidMount() {
    if (process.browser) {
      global.addEventListener('beforeunload', this._onBeforeUnload, true);
    }
  }

  componentWillUnmount() {
    this._isUnmounting = true;
    if (process.browser) {
      global.removeEventListener('beforeunload', this._onBeforeUnload, true);
    }
  }

  _onBeforeUnload = (event) => {
    if (this.dirty) {
      event.preventDefault();
      event.returnValue = '';
    }
  };

  _onSubmit = (formValues, formikBag) => {
    const {
      listingRedirection,
      marketsList,
      sunlocksList,
      onSubmit,
      partnersList,
      dispatch,
      formViewGroup,
      apartmentsList,
      floorPlansListByApartmentId,
      listing: originalListing,
      currentUser,
      accountManager,
      landlord,
      marketingPartnerListing,
    } = this.props;

    this.setState({
      isBusy: true,
    });

    cleanPropertyAccessFields(formValues);

    formValues = trimStringValues(formValues);

    delete formValues.promiseDate;

    const redirectionSourceUrl = (formValues.redirectionSourceUrl || '').split('/').pop();
    delete formValues.redirectionSourceUrl;

    const previousListingSunlock = originalListing
      ? sunlocksList.find(({ listingId }) => listingId === originalListing.id)
      : null;

    let newSunlockRaspberryPiSerialNumber =
      (formValues.sunlockRaspberryPiSerialNumber || '').toLowerCase().trim() || null;

    const newExistingListingSunlock = sunlocksList.find(({ raspberryPiSerialNumber }) => {
      return newSunlockRaspberryPiSerialNumber === raspberryPiSerialNumber;
    });

    const sunlockUpdates = setNullEmptyValues({
      sunlockId: newExistingListingSunlock ? newExistingListingSunlock.id : null,
    });

    const shouldUpdateSunlock =
      newExistingListingSunlock &&
      (sunlockUpdates.lockCode !== newExistingListingSunlock.lockCode ||
        sunlockUpdates.partnerLockCode !== newExistingListingSunlock.partnerLockCode);

    delete formValues.sunlockRaspberryPiSerialNumber;

    const landlordUpdates = {
      name: formValues.landlordName,
      phone: formValues.landlordPhone,
      email: formValues.landlordEmail,
    };

    delete formValues.landlordName;
    delete formValues.landlordPhone;
    delete formValues.landlordEmail;

    const listingLinkFormValues = formValues.listingLinks;

    if (formValues.postToMls === 'false' || formValues.postToMls === false) {
      listingLinkFormValues.mls = '';
      formValues.mlsNumber = null;
      formValues.mlsStatus = null;
      formValues.postToMls = false;
    }

    const srListingLinks =
      listingLinkFormValues &&
      Object.keys(listingLinkFormValues)
        .map((formFieldId) => ({
          siteName: listingLinkFormFieldIdToSiteNameMapping[formFieldId],
          linkUrl: formValues.listingLinks[formFieldId] || '',
          wasReviewed: formValues.listingLinks[`${formFieldId}Reviewed`] || false,
        }))
        .filter(({ siteName }) => siteName);
    delete formValues.listingLinks;

    if (formValues.addressStreetSuffix) {
      formValues.addressStreetNoSuffix = false;
    }

    if (formValues.photoLiveStatus !== MARKETING_PARTNER_LISTING_INSTALL_STATUS_COMPLETED) {
      formValues.photoLiveDate = null;
    }

    const longitude = parseFloat(formValues.locationLongitude, 10);
    const latitude = parseFloat(formValues.locationLatitude, 10);

    let marketId = null;

    if (longitude && latitude) {
      for (const market of marketsList) {
        const point = [longitude, latitude];
        if (pointInsidePolygon(point, market.serviceZone.coordinates)) {
          marketId = market.id;
          break;
        }
      }
    }

    let listing = setNullEmptyValues(formValues);

    // NOTE(@archr): Find a way to update only certain fields based on the view group.
    if (formViewGroup === SR_EDIT_LISTING_FORM_VIEW_GROUP_ALL) {
      formValues.petsAllowed =
        formValues.petsAllowed || formValues.catsAllowed || formValues.dogsAllowed;

      listing = {
        ...listing,
        marketId: marketId,
        addressStreetDisplay: makeListingAddressStreetDisplay(formValues),
        type: 'Residential',
        dataSource: LISTING_DATA_SOURCE_SUNROOM,
        status: formValues.status || LISTING_STATUS_NOT_AVAILABLE,
        updatedDate: new Date().toISOString(),
        listingBrokerageMlsNumber: LISTING_SR_BROKERAGE_MLS_NUMBER,
        listingBroker: LISTING_SR_BROKERAGE_NAME,
        displayType: LISTING_DISPLAY_TYPE_SINGLE_UNIT,
        ...(formValues.petsAllowed ? {} : getPetFieldsToNullify()),
      };
    }

    if (originalListing) {
      for (const listingPropName of Object.keys(listing)) {
        if (listing[listingPropName] === originalListing[listingPropName]) {
          delete listing[listingPropName];
        }
      }
      listing.id = originalListing.id;
      listing.photoLiveDate = originalListing.photoLiveDate;
    }

    const apartment =
      originalListing && apartmentsList.find(({ name }) => name === originalListing.apartmentName);
    const floorPlans = apartment && floorPlansListByApartmentId[apartment.id];
    const floorPlan =
      floorPlans &&
      floorPlans.find(({ tourableApartmentUnits }) => {
        return tourableApartmentUnits.includes(originalListing.apartmentUnit);
      });

    if (
      floorPlan &&
      originalListing &&
      (originalListing.apartmentUnit !== formValues.apartmentUnit ||
        originalListing.floorPlanId !== formValues.floorPlanId) &&
      !global.confirm(
        `This unit is currently the show unit for floorplan ${floorPlan.displayName}. ` +
          'Are you sure you want to change the floorplan?',
      )
    ) {
      this.setState({
        isBusy: false,
      });
      return;
    }

    if (
      originalListing &&
      originalListing.status !== LISTING_STATUS_ACTIVE &&
      formValues.status === LISTING_STATUS_ACTIVE &&
      !currentUser.roles.includes(USER_ROLE_LISTING_REVIEWER)
    ) {
      global.alert(
        'Sorry, you cannot make this listing active. Only Account Managers can make a ' +
          `listing active. Please contact ${accountManager?.name || 'the account manager'} to ` +
          'review this listing and make it active.',
      );
      this.setState({
        isBusy: false,
      });
      return;
    }

    if (
      (formValues.description || '').includes('pets') &&
      !(formValues.description || '').includes('No pets') &&
      !formValues.petsAllowed
    ) {
      global.alert(
        'This listing does not accept pets, but the word "pet" is in the marketing description.' +
          'Confirm if the listing should accept pets or not.' +
          'If it should, update the renter criteria.' +
          'If not, remove the word "pet" from the marketing description.' +
          'This includes links, like the petscreening link.',
      );
      this.setState({
        isBusy: false,
      });
      return;
    }

    debug('Submitting', { listing });

    let updatedListing;

    Promise.resolve()
      .then(() => {
        if (
          previousListingSunlock &&
          (!newSunlockRaspberryPiSerialNumber ||
            previousListingSunlock.raspberryPiSerialNumber !== newSunlockRaspberryPiSerialNumber)
        ) {
          return dispatch(postWithState, {
            action: SR_API_ACTION_SUNLOCK_UNASSIGN_LISTING_ID,
            payload: {
              triggeredBy: currentUser.name,
              sunlockId: previousListingSunlock.id,
            },
          });
        }
      })
      .then(() => {
        if (newExistingListingSunlock && shouldUpdateSunlock) {
          return dispatch(postWithState, {
            action: SR_API_ACTION_SUNLOCK_UPDATE_LOCK_CODES,
            payload: {
              ...sunlockUpdates,
              triggeredBy: currentUser.name,
            },
          });
        }
      })
      .then(() => {
        return dispatch(postWithState, {
          action: SR_API_ACTION_UPSERT_LISTING,
          payload: {
            listing: listing,
          },
        });
      })
      .then((_updatedListing) => {
        updatedListing = _updatedListing;

        if (listingRedirection && listingRedirection.id !== redirectionSourceUrl) {
          return dispatch(postWithState, {
            action: SR_API_ACTION_DELETE_REDIRECTION,
            payload: {
              id: listingRedirection.id,
            },
          });
        }
      })
      .then(() => {
        if (
          listing &&
          redirectionSourceUrl &&
          (!listingRedirection || listingRedirection.id !== redirectionSourceUrl)
        ) {
          return dispatch(postWithState, {
            action: SR_API_ACTION_SET_REDIRECTION,
            payload: {
              id: redirectionSourceUrl,
              url: `/listing/${listing.id}?${LISTING_YARD_SIGN_REDIRECTION_UTM_QUERY_PARAMETERS}`,
            },
          });
        }
      })
      .then(() => {
        if (newSunlockRaspberryPiSerialNumber && !newExistingListingSunlock) {
          global.alert(
            `Sunlock with Serial Number "${newSunlockRaspberryPiSerialNumber}" is not ` +
              'registered. Please register the Sunlock first.',
          );
          newSunlockRaspberryPiSerialNumber = null;
        }
        if (
          newExistingListingSunlock &&
          (!previousListingSunlock || previousListingSunlock.id !== newExistingListingSunlock.id)
        ) {
          return dispatch(postWithState, {
            action: SR_API_ACTION_SUNLOCK_ASSIGN_LISTING_ID,
            payload: {
              triggeredBy: currentUser.name,
              sunlockId: newExistingListingSunlock.id,
              listingId: updatedListing.id,
            },
          });
        }
      })
      .then(() => {
        return dispatch(getSunlocksListAction);
      })
      .then(() => {
        if (srListingLinks) {
          return dispatch(postWithState, {
            action: SR_API_ACTION_UPSERT_LISTING_LINKS,
            payload: {
              listingId: updatedListing.id,
              links: srListingLinks,
              deleteMissingLinks: false,
              replace: true,
            },
          });
        }
      })
      .then(() => {
        return dispatch(getListingLinksAction, {
          listingId: updatedListing.id,
          forceRefresh: true,
        });
      })
      .then(() => {
        return dispatch(actionDataTableReset, {
          dataSource: DATA_TABLE_SOURCE_ADMIN_LISTINGS_LIST,
        });
      })
      .then(() => {
        if (
          landlord &&
          (landlord.name !== landlordUpdates.name ||
            landlord.phone !== landlordUpdates.phone ||
            landlord.email !== landlordUpdates.email)
        ) {
          return dispatch(updateLandlordAction, {
            landlordId: landlord.id,
            updates: landlordUpdates,
          });
        }
      })
      .then(() => {
        const editListingFormFields = makeEditListingFormFields({
          formViewGroup: formViewGroup,
          formValues: formValues,
          partnersList: partnersList,
          marketsList: marketsList,
          sunlocksList: sunlocksList,
          apartmentsList: apartmentsList,
          floorPlansListByApartmentId: floorPlansListByApartmentId,
          partnerId: updatedListing?.partnerId,
          isNew: false,
          currentUser,
        });
        formikBag.resetForm(
          createInitialValues({
            formFields: editListingFormFields,
            defaultValues: {
              ...updatedListing,
              sunlockRaspberryPiSerialNumber: newSunlockRaspberryPiSerialNumber,
              redirectionSourceUrl: redirectionSourceUrl,
              listingLinks: listingLinkFormValues,
              landlordName: this.props.landlord?.name,
              landlordPhone: this.props.landlord?.phone,
              landlordEmail: this.props.landlord?.email,
              promiseDate: getDueDateDisplay(marketingPartnerListing, listing),
            },
          }),
        );

        this.setState({
          showSubmittedLabel: true,
          isBusy: false,
        });
        setTimeout(() => {
          if (!this._isUnmounting) {
            this.setState({
              showSubmittedLabel: false,
            });
          }
        }, 3000);

        onSubmit(updatedListing);
      })
      .catch((error) => {
        this.setState({
          isBusy: false,
        });
        log.error(error);
      });
  };

  _onFindListingByAddressModalSubmit = (listing, formikBag) => {
    const {
      partnersList,
      marketsList,
      sunlocksList,
      apartmentsList,
      formViewGroup,
      currentUser,
    } = this.props;

    this._listingByAddressModalToggle();
    const formFields = makeEditListingFormFields({
      formViewGroup,
      formValues: formikBag.values,
      partnersList: partnersList,
      marketsList: marketsList,
      sunlocksList: sunlocksList,
      apartmentsList: apartmentsList,
      onApartmentBuildingChange: this._makeOnApartmentBuildingChangeFunc(formikBag),
      currentUser,
    });
    const listingAmenities = getListingAmenities(listing);
    const defaultValues = lodashPick(
      formFields.map(({ id }) => id),
      listing,
    );

    for (const listingAmenity of listingAmenities) {
      if (formFields[listingAmenity.name] && formFields[listingAmenity.name].type === 'checkbox') {
        defaultValues[listingAmenity.name] = listingAmenity.value;
      }
    }

    for (const fieldToExclude of fieldsToExcludeFromListingFuzzySearchCopy) {
      defaultValues[fieldToExclude] = null;
    }

    if (apartmentPropertyTypes.includes(listing.propertyType)) {
      defaultValues.apartmentUnit = listing.addressStreetDisplay.split(' ').pop();
    }

    const newFormValues = createInitialValues({
      formFields: formFields,
      defaultValues: defaultValues,
    });

    formikBag.setValues(newFormValues);
  };

  _onRequestStreetSuffixAutocompleteChoices = ({ value }) => {
    const { dispatch } = this.props;

    dispatch(postWithState, {
      action: SR_API_ACTION_FUZZY_SEARCH_ADDRESS_STREET_SUFFIX,
      payload: {
        value,
      },
    })
      .then((streetSuffixMatches) => {
        if (!this._isUnmounting && streetSuffixMatches) {
          this.setState({ streetSuffixMatches });
        }
      })
      .catch(log.error);
  };

  _listingByAddressModalToggle = () => {
    this.setState({
      showPrepopulateListingForm: !this.state.showPrepopulateListingForm,
    });
  };

  _makeOnWalkthroughConditionsChange = (formikBag) => {
    return (newValue) => {
      const previousWalkthroughConditions = formikBag.values.walkthroughConditions;
      if (!Array.isArray(newValue) || newValue.length === 0) {
        formikBag.setFieldValue('walkthroughConditions', newValue);
      } else if (
        !previousWalkthroughConditions.includes(LISTING_WALKTHROUGH_CONDITION_READY) &&
        newValue.includes(LISTING_WALKTHROUGH_CONDITION_READY)
      ) {
        formikBag.setFieldValue('walkthroughConditions', [LISTING_WALKTHROUGH_CONDITION_READY]);
      } else {
        const newValueWithoutReady = newValue.filter((walkthroughCondition) => {
          return walkthroughCondition !== LISTING_WALKTHROUGH_CONDITION_READY;
        });
        formikBag.setFieldValue('walkthroughConditions', newValueWithoutReady);
      }
    };
  };

  _onRequiredFieldsAlertButtonClick = (formikBag) => {
    const {
      partnersList,
      marketsList,
      sunlocksList,
      apartmentsList,
      listing,
      formViewGroup,
      floorPlansListByApartmentId,
      currentUser,
    } = this.props;

    const formFields = makeEditListingFormFields({
      formViewGroup,
      formValues: formikBag.values,
      partnersList: partnersList,
      marketsList: marketsList,
      sunlocksList: sunlocksList,
      apartmentsList: apartmentsList,
      floorPlansListByApartmentId: floorPlansListByApartmentId,
      onApartmentBuildingChange: this._makeOnApartmentBuildingChangeFunc(formikBag),
      isNew: !listing,
      partnerId: listing?.partnerId,
      isTenantScreening: _isTenantScreening(listing),
      isSunRiseListing: _isSunRiseListing(listing),
      currentUser,
    });

    const partner =
      formikBag.values && partnersList
        ? partnersList.find(({ managementCompanyName }) => {
            return formikBag.values.listingManagementCompany === managementCompanyName;
          })
        : null;
    const partnerAmenityRequiredFields =
      partner && partner.amenityPhotosEnabled
        ? getPartnerAmenityRequiredFields({ formValues: formikBag.values })
        : [];
    const apartment =
      partner &&
      formikBag.values.apartmentName &&
      apartmentsList.find(({ partnerId, name }) => {
        return partner.id === partnerId && name === formikBag.values.apartmentName;
      });
    const apartmentSelfShowingFlowRequiredFields =
      apartment && apartment.selfTourFlowEnabled && isSelfShowingListing(formikBag.values)
        ? apartmentSelfTourFlowFields
        : [];

    const requiredActiveListingFieldsFull = [
      ...requiredActiveListingFields,
      ...partnerAmenityRequiredFields,
      ...apartmentSelfShowingFlowRequiredFields,
    ];

    const requiredFormFields = formFields.filter(({ id }) =>
      requiredActiveListingFieldsFull.includes(id),
    );
    const errors = createFormValidationFn(requiredFormFields)(formikBag.values);
    const missingFields = Object.keys(errors);

    for (const fieldId of missingFields) {
      formikBag.setFieldTouched(fieldId, true, true);
    }

    if (missingFields.length === 0) {
      this._onServerValidations(formikBag);
    } else {
      const missingFormFields = formFields
        .filter((formField) => missingFields.includes(formField.id))
        .map((formField) => formField.title);
      global.alert(`Please fill in these missing fields: ${missingFormFields.join(', ')}`);
    }
  };

  async _onServerValidations(formikBag) {
    const { listing, listingRedirection, dispatch } = this.props;

    const formValues = formikBag.values;

    if (formValues.listingManagementCompany) {
      const tenantScreeningCriteria = await dispatch(getTenantScreeningCriteriaAction, {
        managementCompanyName: formValues.listingManagementCompany,
        listingId: listing?.id,
      });
      if (!tenantScreeningCriteria) {
        this._onToggleMissingTenantScreeningCriteriaAlert();
        return;
      }
    }

    const redirectionSourceUrl = (formValues.redirectionSourceUrl || '').split('/').pop();
    if (
      redirectionSourceUrl &&
      (!listingRedirection || listingRedirection.id !== redirectionSourceUrl)
    ) {
      const existingYardSignUrl = await dispatch(postWithState, {
        action: SR_API_ACTION_GET_REDIRECTION,
        payload: {
          id: redirectionSourceUrl,
        },
      });

      if (existingYardSignUrl) {
        if (existingYardSignUrl.includes('listing/')) {
          const listingIdStr = existingYardSignUrl
            .substr(existingYardSignUrl.indexOf('listing/'))
            .split('/')[1];
          const listingId = parseInt(listingIdStr, 10);
          const listing = await dispatch(getListingById, listingId);

          if (listing) {
            const confirmMessage =
              `This url is already in use by ${listing.addressStreetDisplay} - ${listing.id}. ` +
              `The status is ${listing.status}. Are you sure you want to override the url?`;

            if (listing && !global.confirm(confirmMessage)) {
              return;
            }
          }
        } else if (existingYardSignUrl.includes('apartment/')) {
          const apartmentIdStr = existingYardSignUrl
            .substr(existingYardSignUrl.indexOf('apartment/'))
            .split('/')[1];
          const apartmentId = parseInt(apartmentIdStr, 10);
          const apartment = await dispatch(getApartmentById, { apartmentId });

          if (apartment) {
            const confirmMessage =
              `This url is already in use by ${apartment.name} - ${apartment.id}. ` +
              'Are you sure you want to override the url?';
            if (!global.confirm(confirmMessage)) {
              return;
            }
          }
        } else {
          global.alert(
            `This url is already redirecting to the url ${existingYardSignUrl}. ` +
              'Please check with the team before removing the current redirect.',
          );
          return;
        }
      }
    }

    if (
      formValues.igloohomeLockId &&
      (!listing || listing.igloohomeLockId !== formValues.igloohomeLockId)
    ) {
      const listingWithIgloohomeLock = await dispatch(postWithState, {
        action: SR_API_ACTION_LISTING_GET,
        payload: {
          igloohomeLockId: formValues.igloohomeLockId,
        },
      });
      if (listingWithIgloohomeLock) {
        return global.alert(
          'Whoops, the igloohome lock you chose is already in use by another listing, ' +
            `${makeListingFullAddressString(listingWithIgloohomeLock)} - (Listing ID ${
              listingWithIgloohomeLock.id
            }). ` + // eslint-disable-line max-len
            'Please choose another igloohome lock or unassign it from the other listing.',
        );
      }
    }

    formikBag.handleSubmit();
  }

  _makeOnExternalLinksLockedChangeFunc = (formikBag) => {
    return (newExternalLinksLockedValue) => {
      if (newExternalLinksLockedValue === false) {
        return;
      }
      if (!global.confirm('Are you sure the links are correct?')) {
        formikBag.setFieldValue('externalLinksLocked', false);
        return;
      }

      // Check "Validate the urls have the correct root url, and are longer than 5 characters plus the length url"
      const linkFormFieldIds = Object.keys(listingLinkFormFieldIdToSiteNameMapping);
      const listingLinkWebsiteBySiteName = LISTING_LINKS_TO_SCRAPE.reduce(
        (memo, { siteName, website }) => ({
          ...memo,
          [siteName]: website,
        }),
        {},
      );
      const incorrectLinkNames = [];
      for (const linkFormFieldId of linkFormFieldIds) {
        const siteName = listingLinkFormFieldIdToSiteNameMapping[linkFormFieldId];
        const siteNameForComparison = listingLinkWebsiteBySiteName[siteName];
        const linkValue = formikBag.values.listingLinks[linkFormFieldId];
        const parsedUrl = url.parse(linkValue || '');
        if (
          linkValue &&
          ((siteNameForComparison && !parsedUrl.host.includes(siteNameForComparison)) ||
            parsedUrl.path.length <= 5)
        ) {
          incorrectLinkNames.push(siteName);
        }
      }
      if (incorrectLinkNames.length > 0) {
        global.alert(
          `Cannot lock urls. Looks like ${incorrectLinkNames.join(', ')} are ` +
            'incorrect. Please check those urls and try again.',
        );
        formikBag.setFieldValue('externalLinksLocked', false);
      }
    };
  };

  _onPetsAllowedChange = (formikBag) => {
    return (petsAllowed) => {
      if (!petsAllowed) {
        formikBag.setFieldValue('dogsAllowed', false);
        formikBag.setFieldValue('catsAllowed', false);
      }
    };
  };

  _makeOnApartmentChangeFunc = (formikBag) => {
    return (event) => {
      const apartmentName = event.target.value;
      formikBag.setFieldValue('apartmentName', apartmentName);

      if (apartmentName && formikBag.values.listingManagementCompany) {
        const { partnersList, apartmentsList } = this.props;

        const partner = partnersList.find(({ managementCompanyName }) => {
          return formikBag.values.listingManagementCompany === managementCompanyName;
        });
        if (!partner) {
          return;
        }
        const apartment = apartmentsList.find(({ partnerId, name }) => {
          return partner.id === partnerId && name === apartmentName;
        });
        if (!apartment) {
          return;
        }

        formikBag.setFieldValue('apartmentBuildingId', null);
        formikBag.setFieldValue('floorPlanId', null);

        this._getPropertyAttributesHierarchy(
          {
            listingManagementCompany: formikBag.values.listingManagementCompany,
            apartmentName: apartmentName,
            apartmentBuildingId: null,
            floorPlanId: null,
          },
          formikBag,
        );
      }
    };
  };

  _makeOnApartmentBuildingChangeFunc = (formikBag) => {
    const { dispatch } = this.props;
    return (event) => {
      const apartmentBuildingId = parseInt(event.target.value, 10) || null;
      formikBag.setFieldValue('apartmentBuildingId', apartmentBuildingId);

      if (apartmentBuildingId) {
        const { apartmentsList } = this.props;
        for (const apartment of apartmentsList) {
          for (const apartmentBuilding of apartment.apartmentBuildings) {
            if (apartmentBuilding.id === apartmentBuildingId) {
              const addressFieldsObject = {
                addressStreetName: apartmentBuilding.addressStreetName,
                addressStreetSuffix: apartmentBuilding.addressStreetSuffix,
                addressStreetNumber: apartmentBuilding.addressStreetNumber,
                addressZip: apartmentBuilding.addressZip,
                addressCity: apartmentBuilding.addressCity,
                addressStateCode: apartmentBuilding.addressStateCode,
                addressCountryCode: apartmentBuilding.addressCountryCode,
                addressStreetNoSuffix: !apartmentBuilding.addressStreetSuffix,
              };

              Object.keys(addressFieldsObject).forEach((addressFieldKey) => {
                formikBag.setFieldValue(addressFieldKey, addressFieldsObject[addressFieldKey]);
              });

              dispatch(getFloorPlansListByApartmentId, {
                apartmentId: apartment.id,
              });

              this._getPropertyAttributesHierarchy(
                {
                  listingManagementCompany: formikBag.values.listingManagementCompany,
                  apartmentName: formikBag.values.apartmentName,
                  apartmentBuildingId: apartmentBuildingId,
                  floorPlanId: formikBag.values.floorPlanId,
                  ...addressFieldsObject,
                },
                formikBag,
              );
              return;
            }
          }
        }
      }
    };
  };

  _makeOnFloorPlanChangeFunc = (formikBag) => {
    return (event) => {
      const floorPlanId = parseInt(event.target.value, 10) || null;
      formikBag.setFieldValue('floorPlanId', floorPlanId);

      this._getPropertyAttributesHierarchy(
        {
          listingManagementCompany: formikBag.values.listingManagementCompany,
          apartmentName: formikBag.values.apartmentName,
          apartmentBuildingId: formikBag.values.apartmentBuildingId,
          floorPlanId: floorPlanId,
        },
        formikBag,
      );
    };
  };

  _getPropertyAttributesHierarchy = (formValues, formikBag) => {
    const {
      listing,
      formViewGroup,
      sunlocksList,
      partnersList,
      marketsList,
      apartmentsList,
      floorPlansListByApartmentId,
      dispatch,
      currentUser,
    } = this.props;

    const partner = partnersList.find(({ managementCompanyName }) => {
      return formValues.listingManagementCompany === managementCompanyName;
    });
    const apartment = apartmentsList.find(({ partnerId, name }) => {
      return partner.id === partnerId && name === formValues.apartmentName;
    });
    const floorPlan =
      formValues.floorPlanId &&
      floorPlansListByApartmentId[apartment.id].find((floorPlan) => {
        return floorPlan.id === formValues.floorPlanId;
      });

    dispatch(postWithState, {
      action: SR_API_ACTION_PROPERTY_ATTRIBUTE_GET_HIERARCHY,
      payload: {
        apartmentId: apartment.id,
        apartmentBuildingId: formValues.apartmentBuildingId,
        floorPlanId: formValues.floorPlanId,
      },
      onSuccess: (listingFields) => {
        const initialFormValues = createInitialValues({
          formFields: makeEditListingFormFields({
            formViewGroup: formViewGroup,
            formValues: {},
            partnersList: partnersList,
            marketsList: marketsList,
            sunlocksList: sunlocksList,
            apartmentsList: apartmentsList,
            isNew: !listing,
            partnerId: listing?.partnerId,
            isTenantScreening: _isTenantScreening(listing),
            isSunRiseListing: _isSunRiseListing(listing),
            currentUser,
          }).filter((formField) => floorPlanPropertyAttributesFieldgroup.includes(formField.id)),
          defaultValues: {
            ...formikBag.values,
            ...listingFields,
          },
        });

        formikBag.setValues({
          ...formikBag.values,
          ...initialFormValues,
          ...formValues,
          ...(floorPlan ? makeListingPhotosFromFloorPlan(floorPlan) : {}),
        });
      },
    });
  };

  _onListingStatusChange = (formikBag) => {
    const { listing } = this.props;

    return (event) => {
      if (listing?.status === LISTING_STATUS_ACTIVE) {
        return global.alert('Whoops, you need to change the status from the partner portal.');
      }

      formikBag.setFieldValue('status', event.target.value);
    };
  };

  _onToggleMissingTenantScreeningCriteriaAlert = () => {
    this.setState({
      showMissingTenantScreeningCriteriaAlert: !this.state.showMissingTenantScreeningCriteriaAlert,
    });
  };

  _allFieldsAreHidden = (formFields) => {
    return formFields.every((formField) => !!formField.hideFieldIf);
  };

  render() {
    const {
      listing,
      listingLinks,
      listingRedirection,
      partnersList,
      marketsList,
      sunlocksList,
      codeboxLockList,
      apartmentsList,
      dispatch,
      formViewGroup,
      floorPlansListByApartmentId,
      appUrl,
      accountManager,
      leasingManager,
      landlord,
      partnerUsers,
      marketingPartnerListing,
      jobItemsInstalled,
      currentUser,
      preLease,
    } = this.props;

    const {
      isBusy,
      streetSuffixMatches,
      showHiddenFields,
      showPrepopulateListingForm,
      showSubmittedLabel,
      showMissingTenantScreeningCriteriaAlert,
    } = this.state;

    const isScreeningListing = _isTenantScreening(listing);

    const listingSunlock = listing
      ? sunlocksList.find(({ listingId }) => listingId === listing.id)
      : null;

    const listingCodeboxLock = listing
      ? codeboxLockList.find(({ listingId }) => listingId === listing.id)
      : null;

    const formFieldGroups = SR_EDIT_LISTING_FORM_VIEW_GROUP_TO_FORM_FIELD_GROUPS_MAPPING[
      formViewGroup
    ].filter((formFieldGroup) => {
      if (listing?.isSyndicated) return true;

      // Remove this form field group if isSyndicated is false
      return formFieldGroup !== makeEditListingFormFields6;
    });

    const editListingFormFields = makeEditListingFormFields({
      formViewGroup: formViewGroup,
      formValues: {},
      partnersList: partnersList,
      marketsList: marketsList,
      sunlocksList: sunlocksList,
      apartmentsList: apartmentsList,
      isNew: !listing,
      partnerId: listing?.partnerId,
      isTenantScreening: isScreeningListing,
      isSunRiseListing: _isSunRiseListing(listing),
      currentUser,
    });

    const partner = !listing
      ? null
      : partnersList.find(({ id }) => {
          return listing.partnerId === id;
        });

    debug('Rendering form', {
      editListingFormFields,
      sunlocksList,
      listing,
    });

    let contactForQuestionsDOM = null;
    if (
      listing?.listingAgreementSignedBy === MARKETING_PARTNER_LISTING_AGREEMENT_SIGNED_BY_SUNROOM &&
      landlord
    ) {
      // eslint-disable-line max-len
      contactForQuestionsDOM = (
        <SrBox marginTop="20px">
          <SrText font="greycliff" weight="800" color="orange300">
            Referral listing - Contact Landlord for questions. <br />
            <SrText font="inherit" weight="600" color="inherit">
              Landlord: {landlord.name} {landlord.phone} {landlord.email}
            </SrText>
            <SrText font="inherit" weight="600" color="inherit" display="block">
              Client Pays Agent:{' '}
              {listing.commissionPercentClientPays
                ? formatPercentage(listing.commissionPercentClientPays)
                : '--'}
            </SrText>
          </SrText>
        </SrBox>
      );
    } else if (
      listing?.listingAgreementSignedBy !== MARKETING_PARTNER_LISTING_AGREEMENT_SIGNED_BY_SUNROOM &&
      partner &&
      partnerUsers.length
    ) {
      // eslint-disable-line max-len
      let partnerUsersForContact = (partner.type === PARTNER_TYPE_AGENT
        ? partnerUsers
        : lodashCompact([marketingPartnerListing])
      ).filter((partnerUser) => !!partnerUser?.user?.name);

      if (listing.propertyManagerUserId) {
        partnerUsersForContact = partnerUsers.filter(
          ({ user }) => user.id === listing.propertyManagerUserId,
        );
      }

      contactForQuestionsDOM = (
        <SrBox marginTop="10px">
          <SrText font="greycliff" weight="800" color="green">
            Hands-On - Contact Partner for questions <br />
            {partnerUsersForContact.map((partnerUser) => (
              <SrText key={partnerUser.id} font="inherit" weight="600" color="inherit">
                Partner User: {partnerUser.user?.name} {partnerUser.user?.phone}{' '}
                {partnerUser.user?.email}
                <br />
              </SrText>
            ))}
            {partnerUsersForContact.length === 0 && (
              <SrText font="inherit" weight="600" color="inherit">
                Partner User: API
                <br />
              </SrText>
            )}
          </SrText>
        </SrBox>
      );
    }

    let contactForKeyPickupDOM = null;
    if (
      listing?.propertySetupAccessType === PROPERTY_SETUP_ACCESS_TYPE_CONTACT_FOR_KEY &&
      listing?.propertyAccessContactName
    ) {
      contactForKeyPickupDOM = (
        <SrBox marginTop="20px">
          <SrText font="greycliff" weight="800">
            Contact Person For Keys
            <br />
            <SrText font="inherit" weight="600">
              {`${listing.propertyAccessContactName}
              ${listing.propertyAccessContactPhone || ''}
              ${listing.propertyAccessContactEmail || ''}`}
            </SrText>
          </SrText>
        </SrBox>
      );
    }

    return (
      <div className="SrEditListingForm">
        {listing?.postToMls === false &&
          listing?.isSyndicated &&
          formViewGroup !== SR_EDIT_LISTING_FORM_VIEW_GROUP_PHOTOGRAPHERS && (
            <SrBox marginBottom="15px">
              <SrLabel
                color="red"
                backgroundColor="rgba(232, 0, 29, 0.08)"
                iconUrl="/images/caution-icon-red.svg"
                iconDropShadow="0 3px 4px rgba(232, 0, 29, 0.27)"
                fontSize="large"
                label="Do not post to MLS"
              />
            </SrBox>
          )}
        {isScreeningListing && (
          <SrBox marginBottom="15px">
            <SrLabel
              color="green"
              backgroundColor="rgba(0, 162, 56, 0.08)"
              iconUrl="/images/caution-icon-yellow.svg"
              iconDropShadow="0 3px 4px rgba(232, 0, 29, 0.27)"
              fontSize="large"
              label="Tenant-Screening Listing"
            />
          </SrBox>
        )}
        {listing?.postToMls && listingSunlock && !jobItemsInstalled?.supraLockInstalled && (
          <SrBox marginBottom="15px">
            <SrLabel
              color="red"
              backgroundColor="rgba(232, 0, 29, 0.08)"
              iconUrl="/images/caution-icon-red.svg"
              iconDropShadow="0 3px 4px rgba(232, 0, 29, 0.27)"
              fontSize="large"
              label="No Supra - All agents tour using Sunlock. Follow instructions on MLS."
            />
          </SrBox>
        )}
        {formViewGroup === SR_EDIT_LISTING_FORM_VIEW_GROUP_ALL && (
          <div className="flex">
            <div
              className="topButton yellow button"
              onClick={_onScrollToField}
              data-field-id="lockboxType"
            >
              field ops
            </div>
            <div className="topButton yellow button" onClick={this._listingByAddressModalToggle}>
              find listing
            </div>
            {listing && (
              <SrFlex justify="center">
                <SrBox paddingLeft={20}>
                  <SrLink target="_blank" color="blue300" to={`/listing/${listing.id}`}>
                    Renter Page
                  </SrLink>
                </SrBox>
                <SrBox paddingLeft={20}>
                  <SrLink
                    target="_blank"
                    color="blue300"
                    to={`/sunsetup/listing-orders/${listing.id}`}
                  >
                    Field Ops Orders
                  </SrLink>
                </SrBox>
                <SrBox paddingLeft={20}>
                  <SrEditListingTenantScreeningCriteriaCta listing={listing} partner={partner}>
                    <SrText color="blue300" cursor="pointer">
                      Tenant Screening Criteria
                    </SrText>
                  </SrEditListingTenantScreeningCriteriaCta>
                </SrBox>
                {partner && (
                  <>
                    <SrBox paddingLeft={20}>
                      <SrLink target="_blank" color="blue300" to={`/partner/${partner.slug}`}>
                        Partner
                      </SrLink>
                    </SrBox>
                    <SrBox paddingLeft={20}>
                      <SrLink
                        target="_blank"
                        color="blue300"
                        to={`/partner/${partner?.slug}/listing/${listing.id}`}
                      >
                        Partner Listing Page
                      </SrLink>
                    </SrBox>
                  </>
                )}
              </SrFlex>
            )}
          </div>
        )}
        {listing?.isSyndicated === false &&
          formViewGroup !== SR_EDIT_LISTING_FORM_VIEW_GROUP_PHOTOGRAPHERS && (
            <SrBox marginTop="30px">
              <SrLabel
                color="red"
                backgroundColor="rgba(232, 0, 29, 0.08)"
                iconUrl="/images/caution-icon-red.svg"
                iconDropShadow="0 3px 4px rgba(232, 0, 29, 0.27)"
                fontSize="large"
                label="Don't syndicate this listing"
              />
            </SrBox>
          )}
        <SrBox marginTop="30px">
          <SrText font="greycliff" fontSize="24px" weight="800">
            {makeListingFullAddressString(listing)}
          </SrText>
        </SrBox>
        {preLease && isPreLeaseActive({ preLease }) && (
          <SrBox margin="20px 0">
            <SrText color="orange300" weight="800">
              Currently in Pre-Lease. Applications are accepted, but tours are not available.
            </SrText>
            <br />
            <SrText color="orange300" weight="500">
              The home expects tours to be available as soon as{' '}
              {moment(preLease.startDate).format('MMM D')}
            </SrText>
          </SrBox>
        )}
        {formViewGroup !== SR_EDIT_LISTING_FORM_VIEW_GROUP_PHOTOGRAPHERS && (
          <>
            <SrBox marginTop="20px">
              <SrText font="greycliff" weight="800">
                Account Manager:&nbsp;
              </SrText>
              <SrText font="greycliff" weight="600">
                {accountManager?.name || 'null'}
              </SrText>
            </SrBox>
            <SrBox>
              <SrText font="greycliff" weight="800">
                Leasing Operations Manager:&nbsp;
              </SrText>
              {leasingManager ? (
                <SrText font="greycliff" weight="600">
                  {leasingManager.name}
                </SrText>
              ) : (
                <SrText font="greycliff" weight="600">
                  null
                </SrText>
              )}
            </SrBox>
          </>
        )}
        {listingCodeboxLock && (
          <SrBox marginTop="2px">
            <SrText
              color="yellowGold"
              weight="800"
              font="greycliff"
              fontSize="16px"
              lineHeight="19px"
            >
              CodeBox Installed
            </SrText>
          </SrBox>
        )}
        {listingSunlock && (
          <SrBox marginTop="2px">
            <SrText
              color="yellowGold"
              weight="800"
              font="greycliff"
              fontSize="16px"
              lineHeight="19px"
            >
              Sunlock Installed
            </SrText>
          </SrBox>
        )}
        {formViewGroup !== SR_EDIT_LISTING_FORM_VIEW_GROUP_PHOTOGRAPHERS && contactForQuestionsDOM}
        {contactForKeyPickupDOM}
        <Formik
          ref={(formik) => {
            this.formik = formik;
          }}
          initialValues={createInitialValues({
            formFields: editListingFormFields,
            defaultValues: {
              ...(listing || newListingDefaultValues),
              sunlockRaspberryPiSerialNumber: listingSunlock
                ? listingSunlock.raspberryPiSerialNumber || null
                : null,
              redirectionSourceUrl: listingRedirection ? listingRedirection.id : null,
              listingLinks: makeListingLinksFormValuesFromSrListingLinks(listingLinks),
              landlordName: landlord?.name,
              landlordPhone: landlord?.phone,
              landlordEmail: landlord?.email,
              coListingAgentMlsNumber:
                listing?.coListingAgentMlsNumber || partner?.coListingAgentMlsNumber,
              postToMls:
                typeof listing?.postToMls === 'boolean' ? listing.postToMls : partner?.postToMls,
              promiseDate: getDueDateDisplay(marketingPartnerListing, listing),
              propertyManagerUserId: listing?.propertyManagerUserId || null,
            },
          })}
          validate={(values) => {
            const _editListingFormFields = makeEditListingFormFields({
              formViewGroup: formViewGroup,
              isActive: values.status === LISTING_STATUS_ACTIVE,
              yardSignAllowed: values.yardSignAllowed,
              propertyType: values.propertyType,
              walkthroughConditions: values.walkthroughConditions,
              formValues: values,
              partnersList: partnersList,
              marketsList: marketsList,
              sunlocksList: sunlocksList,
              apartmentsList: apartmentsList,
              isNew: !listing,
              partnerId: listing?.partnerId,
              isTenantScreening: isScreeningListing,
              isSunRiseListing: _isSunRiseListing(listing),
              currentUser,
            });
            return createFormValidationFn(_editListingFormFields)(values);
          }}
          enableReinitialize
          validateOnBlur
          validateOnChange={false}
          onSubmit={this._onSubmit}
          render={(formikBag) => {
            const formValues = formikBag.values;
            this.dirty = formikBag.dirty;
            const propertyAttributes = this.props.propertyAttributes.filter(
              ({ apartmentBuildingId, floorPlanId }) =>
                (!apartmentBuildingId || apartmentBuildingId === formValues.apartmentBuildingId) &&
                (!floorPlanId || floorPlanId === formValues.floorPlanId),
            );

            return (
              <Form autoComplete="off">
                {showPrepopulateListingForm && (
                  <SrFindListingByAddressModal
                    initialValue={`${formValues.addressStreetNumber || ''} ${
                      formValues.addressStreetName || ''
                    }`.trim()}
                    onSubmit={(listing) => {
                      this._onFindListingByAddressModalSubmit(listing, formikBag);
                    }}
                    onClose={this._listingByAddressModalToggle}
                    dispatch={dispatch}
                  />
                )}
                {formFieldGroups.map((sectionFormFieldsGetter, index) => {
                  const sectionFormFields = withDisabledFieldIdPropertyAttributeIsOverride(
                    sectionFormFieldsGetter({
                      formViewGroup: formViewGroup,
                      formValues: formValues,
                      partnersList: partnersList,
                      marketsList: marketsList,
                      partnerUsers: partnerUsers,
                      apartmentsList: apartmentsList,
                      sunlocksList: sunlocksList,
                      landlord: landlord,
                      isSunRiseListing: _isSunRiseListing(listing),
                      floorPlansListByApartmentId: floorPlansListByApartmentId,
                      onExternalLinksLockedChange: this._makeOnExternalLinksLockedChangeFunc(
                        formikBag,
                      ),
                      onApartmentChange: this._makeOnApartmentChangeFunc(formikBag),
                      onApartmentBuildingChange: this._makeOnApartmentBuildingChangeFunc(formikBag),
                      onFloorPlanChange: this._makeOnFloorPlanChangeFunc(formikBag),
                      onPetsAllowedChange: this._onPetsAllowedChange(formikBag),
                      onListingStatusChange: this._onListingStatusChange(formikBag),
                      streetSuffixMatches: streetSuffixMatches,
                      showHiddenFields: showHiddenFields,
                      requestStreetAutocompleteChoices: this
                        ._onRequestStreetSuffixAutocompleteChoices,
                      onWalkthroughConditionsChange: this._makeOnWalkthroughConditionsChange(
                        formikBag,
                      ),
                      partnerId: listing?.partnerId,
                      currentUser,
                    }),
                    propertyAttributes,
                  );

                  const allSectionFieldsHidden = this._allFieldsAreHidden(sectionFormFields);
                  if (allSectionFieldsHidden) {
                    return null;
                  }

                  return (
                    <div
                      className={classnames(
                        'formSection',
                        index === 0 ? 'first' : null,
                        `formSection-${sectionFormFieldsGetter.name}`,
                      )}
                      key={index /* eslint-disable-line react/no-array-index-key */}
                    >
                      {sectionFormFields.map((formField) => (
                        <>
                          {formField.id === 'description' &&
                            preLease &&
                            isPreLeaseActive({ preLease }) && (
                              <SrBox marginBottom="8px">
                                <SrText color="orange300" weight="800">
                                  Do not write about the PreLease! Our system adds that
                                  automatically.
                                </SrText>
                              </SrBox>
                            )}
                          <SrFormikField
                            key={formField.id}
                            formField={formField}
                            formikBag={formikBag}
                          />
                        </>
                      ))}
                    </div>
                  );
                })}
                <ButtonDeprecated
                  theme={BUTTON_THEME_YELLOW}
                  onClick={() => {
                    if (listing && formValues.status === LISTING_STATUS_ACTIVE) {
                      this._onRequiredFieldsAlertButtonClick(formikBag);
                    } else {
                      this._onServerValidations(formikBag);
                    }
                  }}
                  disabled={showSubmittedLabel || !formikBag.dirty}
                  className="button-bottom-sticky"
                >
                  {isBusy
                    ? 'please wait...'
                    : showSubmittedLabel
                    ? 'saved!'
                    : listing
                    ? 'update'
                    : 'create'}
                </ButtonDeprecated>

                {showMissingTenantScreeningCriteriaAlert && (
                  <SrSimpleMessageModal
                    iconUrl="/images/caution-red.svg"
                    message={
                      <>
                        <span>
                          Whoops, this partner ({formValues.listingManagementCompany}) does not have
                          any tenant screening criteria.
                          <br />
                          <br />
                          Please create the partner's tenant screening criteria before activating
                          their listings:
                          <br />
                          <br />
                          <SrLink
                            to={`${appUrl}/admin/partner-tenant-screening-criteria`}
                            target="_blank"
                          >
                            <SrText color="blue300">
                              {appUrl}/admin/partner-tenant-screening-criteria
                            </SrText>
                          </SrLink>
                        </span>
                      </>
                    }
                    buttonText="ok, thanks"
                    maxWidth={375}
                    onClose={this._onToggleMissingTenantScreeningCriteriaAlert}
                  />
                )}
                <SrRouteLeavingGuard
                  when={formikBag.dirty}
                  dispatch={dispatch}
                  headline="You have unsaved changes to this listing. Press ‘Update’ below to save them."
                />
              </Form>
            );
          }}
        />
        {listing && (
          <div className="formSection jc-start">
            <SrText size="xl" font="whitney-sc">
              3D Tour or Video
            </SrText>
            <SrDataTableMedia listingId={listing.id}>
              {({ mediaList, reloadMediaList }) => (
                <SrAdminMediaListTable
                  mediaList={mediaList}
                  reloadMediaList={reloadMediaList}
                  dispatch={dispatch}
                  createMediaDefaultValues={{
                    listingId: listing.id,
                    thumbnailUrl:
                      listing.photoUrls[4] || listing.photoUrls[listing.photoUrls.length - 1],
                  }}
                  createMediaTitle={`Media for ${listing.addressStreetDisplay}`}
                />
              )}
            </SrDataTableMedia>
          </div>
        )}
        {
          <SrBox maxWidth="600px" marginTop="150px">
            <SrButton
              theme={SR_BUTTON_THEME_ADOBE_YELLOW}
              onClick={() => {
                this.setState({
                  showHiddenFields: !showHiddenFields,
                });
              }}
            >
              {showHiddenFields ? 'Collapse ' : 'Expand '} All Fields
            </SrButton>
          </SrBox>
        }
      </div>
    );
  }
}

SrEditListingForm.displayName = 'SrEditListingForm';

export default SrEditListingForm;
